/**
 * PdfActivityViewer — Fase 1 (custom viewer)
 *
 * Reescrito usando `pdfjs-dist` directo porque `@react-pdf-viewer/core@3.12`
 * NO renderiza anotaciones tipo Widget (AcroForms), lo que hacía que los
 * campos de texto/dropdown/checkbox se vieran como imagen plana.
 *
 * Este visor:
 *  - Renderiza cada página con canvas + capa de anotaciones interactiva
 *    (renderForms: true → AcroForms rellenables como HTML inputs reales).
 *  - Autoguarda respuestas (debounce 500ms) → POST /api/activities/draft.
 *  - Botón "Entregar actividad" → POST /api/activities/submit.
 *  - Toolbar propia según guía del cliente (líneas verdes: mantener; rojas: ocultar).
 *
 * Cero IA. Cero corrección local. Cero detección de micrófono.
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/web/pdf_viewer.css';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Save, Send, CheckCircle, ChevronLeft, ChevronRight, ZoomIn, ZoomOut,
  Search, LayoutGrid, Moon, Sun, Hand, MoreVertical, MousePointer2,
  Play, Pause, X, Highlighter, Trash2, Volume2, Mic,
} from 'lucide-react';
import axios from 'axios';
import { AudioExerciseOverlay } from './AudioExerciseOverlay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same pdfjs version we installed (3.4.120). Worker MUST match the API version.
const PDFJS_WORKER = 'https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js';
pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;

const CMAP_URL = 'https://unpkg.com/pdfjs-dist@3.4.120/cmaps/';
const STANDARD_FONTS_URL = 'https://unpkg.com/pdfjs-dist@3.4.120/standard_fonts/';

const DEFAULT_SCALE = 1.25;
const MIN_SCALE = 0.5;
const MAX_SCALE = 3.5;

/** Minimal linkService required by AnnotationLayer.render. */
const noopLinkService = {
  externalLinkTarget: 2,
  externalLinkRel: 'noopener noreferrer nofollow',
  externalLinkEnabled: true,
  isInPresentationMode: false,
  addLinkAttributes: () => {},
  getDestinationHash: () => '#',
  getAnchorUrl: () => '#',
  setHash: () => {},
  executeNamedAction: () => {},
  cachePageRef: () => {},
  navigateTo: () => {},
  goToDestination: () => {},
  goToPage: () => {},
};

export function PdfActivityViewer({
  contentId, pdfUrl, token, onSubmitted,
  review = null, readOnly = false,
  // Fase 2 · manual correction from the student (2nd pass).
  // When correctionMode=true, the viewer keeps FH overlays visible AND allows
  // the student to edit AcroForm widgets again; the primary CTA changes to
  // "Enviar corrección" and POSTs to /manual-correction/{correctionActivityId}
  // — no call to Feedback Hub, no second auto-correct.
  correctionMode = false,
  correctionActivityId = null,
  // Fase 2 · Teacher-only manual status override. When true, each FH
  // overlay exposes small "Marcar Correcto / Revertir a FH" actions. All
  // actions are AE-only (no Feedback Hub round-trip).
  allowOverride = false,
  overrideActivityId = null,
  onOverrideChanged,
  // iter74 · Lecciones / Tareas / Refuerzo Académico only: require every
  // detected mic recording to be saved before allowing «Entregar actividad».
  requireAudioBeforeSubmit = false,
}) {
  const containerRef = useRef(null);
  const pdfDocRef = useRef(null);
  const pageRefs = useRef({}); // pageNum -> DOM node
  const renderTasksRef = useRef({}); // pageNum -> RenderTask (to cancel)
  // Fase 2 · pageNum (1-based) -> { widthPt, heightPt } in PDF points.
  // Populated at render time via `page.getViewport({scale:1})`, and used by
  // submitActivity to translate the student's highlighter marks
  // (stored as display-pixel percentages) into PDF-point rectangles for
  // Feedback Hub graphic activities (Tipo B).
  const pageDimsRef = useRef({});
  const answersRef = useRef({}); // live answers, keyed by "p{page}::{name}"
  const debounceRef = useRef(null);
  const initialDraftRef = useRef(null); // { map, submitted, activity_id }
  // Kept in sync with the `readOnly` prop so the memoised renderPage
  // closure (deps: []) can freeze widgets without stale-value issues.
  const readOnlyRef = useRef(readOnly);
  useEffect(() => { readOnlyRef.current = readOnly; }, [readOnly]);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saveState, setSaveState] = useState('idle'); // idle|saving|saved|error
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [showThumbs, setShowThumbs] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [tool, setTool] = useState('select'); // 'select' | 'hand'
  const [moreOpen, setMoreOpen] = useState(false);
  const [scrollMode, setScrollMode] = useState('vertical'); // vertical | horizontal | wrapped
  const [showSearch, setShowSearch] = useState(false);
  // Fase 2 · Correction overlays derived from Feedback Hub `review.fields[]`.
  // Grouped per page for O(1) render lookup. rect = [x0, y0, x1, y1] in PDF pt.
  // IMPORTANT: FH page indexes are 0-BASED; the viewer's pageNum is 1-BASED
  // (pdfjs convention). We normalize here at the render boundary so the whole
  // component stays 1-based downstream.
  const reviewFieldsByPage = useMemo(() => {
    const grouped = {};
    const fields = review?.review?.fields || review?.fields || [];
    fields.forEach((f, idx) => {
      const key = (typeof f.page === 'number' ? f.page : 0) + 1;
      (grouped[key] = grouped[key] || []).push({ ...f, _idx: idx });
    });
    return grouped;
  }, [review]);
  const reviewSummary = review?.review?.summary || review?.summary || null;
  // Fase 2 · Feedback Hub graphic answers (Tipo B / CROSS OUT). Same
  // rect coordinate space as review.fields[] but grouped inside
  // `review.graphic_answers.results[]`. Each entry has:
  //   {page (0-based), question (int), status, selected_option_id,
  //    correct_option_id, options:[{option_id, rect:[x0,y0,x1,y1],
  //    is_correct, coverage}, ...], teacher_overridden?, original_status?}
  // The bounding rect used for the visual overlay is the CORRECT option's
  // rect (falling back to the union of all option rects if not found), so
  // the overlay sits over the answer the student was expected to cross out.
  const graphicResultsByPage = useMemo(() => {
    const grouped = {};
    const gaResults =
      review?.review?.graphic_answers?.results ||
      review?.graphic_answers?.results ||
      [];
    gaResults.forEach((g, idx) => {
      const key = (typeof g.page === 'number' ? g.page : 0) + 1;
      // Compute a stable bounding rect for the overlay.
      const opts = Array.isArray(g.options) ? g.options : [];
      // Union of every option rect. This is the click target for the
      // docente's override popup (iter68 §9e): with the decoupled
      // contract we paint one coloured box per option and hide the
      // outer overlay's fill, so the outer div must span ALL options
      // to catch clicks on any of them (green OR red). For legacy
      // single-option questions the union is equivalent to that lone
      // option's rect.
      let unionRect = null;
      if (opts.length) {
        const xs0 = [], ys0 = [], xs1 = [], ys1 = [];
        for (const o of opts) {
          if (Array.isArray(o?.rect) && o.rect.length === 4) {
            xs0.push(Number(o.rect[0])); ys0.push(Number(o.rect[1]));
            xs1.push(Number(o.rect[2])); ys1.push(Number(o.rect[3]));
          }
        }
        if (xs0.length) unionRect = [Math.min(...xs0), Math.min(...ys0), Math.max(...xs1), Math.max(...ys1)];
      }
      // Legacy visual anchor: the correct option's own rect. Kept as
      // fallback for reviews without an options[] payload at all.
      let overlayRect = unionRect;
      if (!overlayRect && g.correct_option_id) {
        const co = opts.find(o => o?.option_id === g.correct_option_id);
        if (co && Array.isArray(co.rect) && co.rect.length === 4) overlayRect = co.rect;
      }
      const selectedRect = (() => {
        if (!g.selected_option_id) return null;
        const so = opts.find(o => o?.option_id === g.selected_option_id);
        return so && Array.isArray(so.rect) && so.rect.length === 4 ? so.rect : null;
      })();
      (grouped[key] = grouped[key] || []).push({
        ...g,
        _idx: idx,
        _overlayRect: overlayRect,
        _selectedRect: selectedRect,
      });
    });
    return grouped;
  }, [review]);
  const graphicSummary =
    review?.review?.graphic_answers?.summary ||
    review?.graphic_answers?.summary ||
    null;

  // ─── Fase 3 · Audio exercises overlay integration ──────────────────────
  // Sources of truth for `audio_exercises[]`, in order:
  //   1. `postSubmitAudio` — freshly returned by POST /submit-activity for
  //      the student. Coordinates are ONLY known after FH runs the visual
  //      mic detection on submit (contract §12), so the student's recording
  //      panel appears here as an aditional step *after* Entregar.
  //   2. `review.audio_exercises` — persisted per activity, used by the
  //      teacher's review dialog and by the student's «Correcciones del
  //      tutor» viewer.
  // Both shapes match FH's `audio_exercises[i]` — with `x, y, width,
  // height` in PDF points (top-left origin).
  const [postSubmitAudio, setPostSubmitAudio] = useState(null); // {activity_id, audio_exercises[]}
  // Bump this to force <audio> tags + persisted `audio_exercises[]` to
  // refresh after an upload succeeds. We refetch the review or, for the
  // student pre-review flow, just increment attempt counters.
  const [audioRefresh, setAudioRefresh] = useState(0);

  // Fase 3 · pre-submit recorder support. Feedback Hub does NOT expose
  // audio coordinates per bank (contract §13). AE reuses coordinates
  // cached from ANY prior submission of the same bank via a new endpoint
  // `/api/activities/audio-exercises-preview/{content_id}`. If the bank
  // has ever been submitted before, the student sees the recorder from
  // the moment they open the activity — before pressing «Entregar». The
  // recordings are kept LOCALLY (as Blob objects in state) until the
  // student clicks «Entregar actividad», at which point they're uploaded
  // to the resulting activity_id together with the rest of the entrega.
  const [previewAudioExercises, setPreviewAudioExercises] = useState(null); // array | null
  // Map of `exercise_id → { blob, mime, filename, durationSec }` kept in
  // memory during the pre-submit phase.
  const [preSubmitBlobs, setPreSubmitBlobs] = useState({});
  // Number of audios currently being uploaded after a successful submit.
  const [preSubmitUploadRemaining, setPreSubmitUploadRemaining] = useState(0);

  // Only fetch the bank-level cache in the student's pre-submit flow.
  // Skip when the viewer is in read-only, correction or teacher review
  // mode — those already receive `review.audio_exercises`.
  const shouldFetchPreviewAudio =
    !readOnly && !correctionMode && !allowOverride && !review && !!contentId && !!token;
  useEffect(() => {
    if (!shouldFetchPreviewAudio) return;
    let cancelled = false;
    axios.get(`${API}/activities/audio-exercises-preview/${contentId}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (cancelled) return;
        const list = res?.data?.audio_exercises || [];
        setPreviewAudioExercises(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setPreviewAudioExercises([]);
      });
    return () => { cancelled = true; };
  }, [shouldFetchPreviewAudio, contentId, token]);

  const audioMode = useMemo(() => {
    if (allowOverride) return 'teacher-record';           // teacher reviewing an activity
    if (correctionMode) return 'student-record';          // iter71.8 · student in «Correcciones del tutor» → allow recording missed audios
    if (postSubmitAudio) return 'student-record';         // student, after Entregar, recording remaining audios
    // Pre-submit: recorder is enabled from viewer open, using coords cached
    // per bank_id. Recordings stay local (blob) until submit.
    if (previewAudioExercises && previewAudioExercises.length > 0 && !submitted) return 'student-pre-submit';
    return null;
  }, [allowOverride, correctionMode, postSubmitAudio, previewAudioExercises, submitted]);

  const audioActivityId = useMemo(() => {
    if (allowOverride) return overrideActivityId;
    if (correctionMode) return correctionActivityId;
    if (postSubmitAudio) return postSubmitAudio.activity_id;
    // Pre-submit has no activity yet — the overlay uses a null activityId
    // and stores blobs locally through the `onLocalBlobChange` callback.
    return null;
  }, [allowOverride, correctionMode, postSubmitAudio, overrideActivityId, correctionActivityId]);

  const audioExercisesByPage = useMemo(() => {
    const grouped = {};
    const list =
      (postSubmitAudio && postSubmitAudio.audio_exercises) ||
      review?.review?.audio_exercises ||
      review?.audio_exercises ||
      previewAudioExercises ||
      [];
    list.forEach(a => {
      const key = (typeof a.page === 'number' ? a.page : 0) + 1;
      // Attach any local blob preview so the overlay can render the
      // pre-submit playback player without reaching to the parent.
      const localBlob = preSubmitBlobs[a.exercise_id] || null;
      (grouped[key] = grouped[key] || []).push({ ...a, _preSubmitLocal: localBlob });
    });
    return grouped;
    // audioRefresh included to bust memo after uploads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postSubmitAudio, review, audioRefresh, previewAudioExercises, preSubmitBlobs]);

  // Callback used by AudioExerciseOverlay in pre-submit mode: whenever the
  // student finishes a local recording (or discards it), we keep or drop
  // the entry in `preSubmitBlobs`. The blob is uploaded later, at submit.
  // We also create a stable object URL so the persisted-audio player
  // survives overlay re-mounts (e.g. when the user scrolls pages).
  const handlePreSubmitBlobChange = useCallback((exerciseId, blobInfo) => {
    setPreSubmitBlobs(prev => {
      // Revoke any previous URL for this exercise to free memory.
      const previous = prev[exerciseId];
      if (previous && previous.blobUrl) {
        try { URL.revokeObjectURL(previous.blobUrl); } catch { /* noop */ }
      }
      const next = { ...prev };
      if (blobInfo && blobInfo.blob) {
        const url = URL.createObjectURL(blobInfo.blob);
        next[exerciseId] = { ...blobInfo, blobUrl: url };
      } else {
        delete next[exerciseId];
      }
      return next;
    });
  }, []);

  // Ensure we clean up any lingering blob URLs when the viewer unmounts.
  useEffect(() => () => {
    Object.values(preSubmitBlobs).forEach(info => {
      if (info && info.blobUrl) {
        try { URL.revokeObjectURL(info.blobUrl); } catch { /* noop */ }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAudioUploaded = useCallback((fhResponse) => {
    // Bump the refresh counter so any <audio> tag that reads
    // `${API}/activities/audio/{id}/file` re-mounts and picks up the
    // newest `student_audio` / `teacher_audio` when the parent refetches.
    setAudioRefresh(v => v + 1);
    // Fase 3 · student-record path (post-submit) has no `review` refetch
    // — patch the local `postSubmitAudio.audio_exercises[]` with the new
    // audio_id so the persisted player renders without waiting.
    if (fhResponse && fhResponse.exercise_id && fhResponse.audio_id) {
      setPostSubmitAudio(prev => {
        if (!prev || !Array.isArray(prev.audio_exercises)) return prev;
        return {
          ...prev,
          audio_exercises: prev.audio_exercises.map(ex => {
            if (ex.exercise_id !== fhResponse.exercise_id) return ex;
            const role = fhResponse.role;
            const patch = { audio_id: fhResponse.audio_id, attempt: fhResponse.attempt };
            if (role === 'teacher') return { ...ex, teacher_audio: patch };
            // default to student
            return { ...ex, student_audio: patch };
          }),
        };
      });
    }
    // Teacher/student-view rely on `onOverrideChanged` (already wired by
    // parent dialog) to refetch /review — audio exercises will be
    // refreshed with the new persisted `audio_id` in the response.
    if (onOverrideChanged) onOverrideChanged();
  }, [onOverrideChanged]);
  // Fase 2 · Persisted student highlighter marks (Tipo B graphic activities),
  // returned by Feedback Hub's /review as highlights.student[] — SAME rect
  // coordinate space (PDF points, top-left origin) as review.fields[] above.
  // Teacher/review mode (readOnly=true, no live drawing session) never
  // populates the session-only `highlightsRef`, so without this the marks
  // the student actually drew are invisible to the teacher even though FH
  // stores and grades them correctly. 0-based FH page -> 1-based pageNum,
  // same convention as reviewFieldsByPage above.
  const reviewHighlightsByPage = useMemo(() => {
    const grouped = {};
    const marks = review?.highlights?.student || review?.review?.highlights?.student || [];
    marks.forEach((m, idx) => {
      const key = (typeof m.page === 'number' ? m.page : 0) + 1;
      (grouped[key] = grouped[key] || []).push({ ...m, _idx: idx });
    });
    return grouped;
  }, [review]);
  // iter70 · Visual highlights (FIND, subrayados, anotaciones libres): NO
  // llevan corrección automática y viven en `db.activities.visual_highlights`.
  // Se cargan tanto de la respuesta de submit como de /review y son
  // editables por el docente/admin en modo review vía
  // POST /activity/{id}/review-visual-highlight. El alumno solo puede
  // verlos tras entregar (nunca los edita post-submit).
  const [visualHighlights, setVisualHighlights] = useState([]);
  useEffect(() => {
    const fromReview = review?.visual_highlights;
    const fromSubmit = submitted?.visual_highlights;
    if (Array.isArray(fromReview)) setVisualHighlights(fromReview);
    else if (Array.isArray(fromSubmit)) setVisualHighlights(fromSubmit);
  }, [review, submitted]);
  const visualHighlightsByPage = useMemo(() => {
    const grouped = {};
    (visualHighlights || []).forEach((h) => {
      const key = (typeof h.page === 'number' ? h.page : 0) + 1;
      (grouped[key] = grouped[key] || []).push(h);
    });
    return grouped;
  }, [visualHighlights]);
  const canManageVisualHighlights = allowOverride && !!overrideActivityId;
  // iter71 · `headers` used to live below this block, which crashed the
  // whole viewer with a white screen because `postVisualHighlightAction`
  // captured it in its deps array before initialization (TDZ ReferenceError).
  // Move it above so the useCallback below closes over a real value.
  const headers = { Authorization: `Bearer ${token}` };
  const postVisualHighlightAction = useCallback(async (action, highlight) => {
    if (!canManageVisualHighlights) return;
    try {
      const res = await axios.post(
        `${API}/activities/activity/${overrideActivityId}/review-visual-highlight`,
        { action, highlight },
        { headers },
      );
      if (Array.isArray(res.data?.visual_highlights)) {
        setVisualHighlights(res.data.visual_highlights);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar el resaltado visual');
    }
  }, [canManageVisualHighlights, overrideActivityId, headers]);
  const [openCommentId, setOpenCommentId] = useState(null);
  // Force re-render of overlays after page shells are created / re-rendered
  const [overlaysReady, setOverlaysReady] = useState(0);
  // Audio (embedded in PDF)
  const [audios, setAudios] = useState([]); // [{index, page, x, y, w, h, page_width, page_height, mime, has_bytes, label, kind}]
  const [audiosError, setAudiosError] = useState(null);
  const [activeAudio, setActiveAudio] = useState(null); // {index, url, label}
  // iter69 · sidebar recorder pattern — one open recorder at a time.
  // `openRecorderId` is the AE/FH exercise_id currently expanded; the
  // sidebar host DOM node is created lazily inside the viewer root so
  // the panel stays put during PDF scroll (identical UX to the audio
  // listening player which is anchored outside the pages container).
  const [openRecorderId, setOpenRecorderId] = useState(null);
  const [recorderSidebarHost, setRecorderSidebarHost] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const audioElRef = useRef(null);
  // Highlighter (session-only, not persisted)
  const [highlighterActive, setHighlighterActive] = useState(false);
  const [highlightPickerOpen, setHighlightPickerOpen] = useState(false);
  const [highlightColor, setHighlightColor] = useState('#facc15');  // yellow-400
  const [highlightMode, setHighlightMode] = useState('rect');       // 'rect' | 'free'
  const [highlightThickness, setHighlightThickness] = useState(12); // px at 100% zoom
  const highlightsRef = useRef({}); // { [pageNum]: [{id, kind, color, ...}] }
  const [highlightsVersion, setHighlightsVersion] = useState(0); // trigger re-renders
  const drawingRef = useRef(null); // rect: {pageNum, startX, startY, currentX, currentY} | free: {pageNum, points:[[x,y]...]}
  const highlightBtnRef = useRef(null);
  const highlightPickerContainerRef = useRef(null);

  // ── 1. Fetch existing draft (before loading PDF, so we can seed the form) ──
  // Fase 2 · Preload precedence (widgets get the topmost non-empty source):
  //   1. review.manual_correction.answers → student re-editing their 2nd pass
  //      (or teacher reviewing a received manual correction, readOnly).
  //   2. review.original_answers → student's 1st pass, used both by
  //      correctionMode (starting point) and by the teacher's review mode
  //      so the widgets display what the student actually submitted.
  //   3. GET /activities/draft/{content_id} → only relevant before submit.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Helper: convert a list of {page:0-based, name, value} into our
      // internal answersRef map keyed by 1-based `p{pg}::name`.
      const seedFromAnswers = (answers) => {
        const map = {};
        (answers || []).forEach(a => {
          map[`p${(a.page ?? 0) + 1}::${a.name}`] = a.value;
        });
        answersRef.current = map;
        initialDraftRef.current = {
          answers: (answers || []).map(a => ({ ...a, page: (a.page ?? 0) + 1 })),
        };
      };
      // Precedence 1 — manual correction (received). Teacher review-received
      // path also lands here, letting the widgets render the student's 2nd
      // pass.
      const mcAnswers = review?.manual_correction?.answers;
      if (mcAnswers && Array.isArray(mcAnswers) && mcAnswers.length) {
        seedFromAnswers(mcAnswers);
        return;
      }
      // Precedence 2 — original submitted answers exposed by /review. In
      // correctionMode this becomes the student's starting point; in review
      // mode it lets the teacher see the original responses.
      const original = review?.original_answers;
      if (original && Array.isArray(original) && original.length) {
        seedFromAnswers(original);
        return;
      }
      // Precedence 3 — draft (autosave, pre-submit only).
      try {
        const res = await axios.get(`${API}/activities/draft/${contentId}`, { headers });
        if (cancelled) return;
        const draft = res.data || {};
        const map = {};
        (draft.answers || []).forEach(a => { map[`p${a.page}::${a.name}`] = a.value; });
        answersRef.current = map;
        initialDraftRef.current = draft;
        if (!correctionMode && draft.submitted && draft.activity_id) {
          setSubmitted({ activity_id: draft.activity_id, alreadyDone: true });
        }
      } catch {
        initialDraftRef.current = { answers: [] };
      }
    })();
    return () => { cancelled = true; };
    // Depend on `review` too — otherwise the manual/original branches never
    // run when the parent mounts us before /review resolves. (PdfActivityDialog
    // now waits for /review before mounting, but the effect still needs to
    // re-run when the review payload arrives late in edge cases.)
  }, [contentId, correctionMode, review]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 2. Load the PDF document ──
  useEffect(() => {
    let cancelled = false;
    let doc = null;
    setLoading(true); setLoadError(null);
    (async () => {
      try {
        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          cMapUrl: CMAP_URL,
          cMapPacked: true,
          standardFontDataUrl: STANDARD_FONTS_URL,
          isEvalSupported: false, // form JS actions disabled (we're purely rendering)
        });
        doc = await loadingTask.promise;
        if (cancelled) { doc.destroy(); return; }
        pdfDocRef.current = doc;
        setNumPages(doc.numPages);
        setCurrentPage(1);
        setPageInput('1');
        setLoading(false);
      } catch (err) {
        if (!cancelled) setLoadError(err?.message || 'No se pudo cargar el PDF');
      }
    })();
    return () => {
      cancelled = true;
      // Cancel any in-flight page renders
      Object.values(renderTasksRef.current).forEach(t => { try { t?.cancel(); } catch { /* noop */ } });
      renderTasksRef.current = {};
      if (doc) { try { doc.destroy(); } catch { /* noop */ } }
      pdfDocRef.current = null;
    };
  }, [pdfUrl]);

  // ── 2b. Fetch embedded audios (Sound / FileAttachment / Movie / etc.) ──
  useEffect(() => {
    if (loading || !numPages) return;
    let cancelled = false;
    setAudiosError(null);
    axios.get(`${API}/activities/content/${contentId}/audios`, { headers })
      .then(res => { if (!cancelled) setAudios(res.data?.audios || []); })
      .catch(err => {
        if (cancelled) return;
        setAudios([]);
        // Show a warning when the server signalled a real problem:
        //   * 5xx / 502 → PDF source unavailable / extraction crashed
        //   * 404 with detail containing "PDF file not available" → cloud+local
        //     both returned empty (key rotated, credit depleted, or file gone)
        // A plain 404 with detail "No audios found in this PDF" is normal for
        // PDFs without audio and stays silent.
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || '';
        if (status && status >= 500) {
          setAudiosError(`No pude leer los audios del PDF (HTTP ${status}). Recarga la actividad o avisa al docente si persiste.`);
        } else if (status === 404 && /PDF (file|source) (bytes )?not available|PDF source unavailable/i.test(detail)) {
          setAudiosError('El archivo PDF no está disponible en el almacenamiento en este momento. Pide al docente que vuelva a subirlo o recargue la lección.');
        }
      });
    return () => { cancelled = true; };
  }, [loading, numPages, contentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 3. Seed the annotationStorage with existing draft answers (once we have a doc) ──
  const seedAnnotationStorage = useCallback(() => {
    const doc = pdfDocRef.current;
    const draft = initialDraftRef.current;
    if (!doc || !draft?.answers?.length) return;
    const storage = doc.annotationStorage;
    draft.answers.forEach(a => {
      // pdfjs uses a numeric annotation id ("{objRef}R") as storage key. We don't
      // have it here (we saved by field name), so seeding is best-effort:
      // we broadcast the value under both the "name" and a "name+page" fallback.
      try { storage.setValue(a.name, { value: a.value }); } catch { /* noop */ }
    });
  }, []);

  // ── 4. Render a single page (canvas + annotation layer with forms) ──
  const renderPage = useCallback(async (pageNum, targetScale) => {
    const doc = pdfDocRef.current;
    const node = pageRefs.current[pageNum];
    if (!doc || !node) return;

    // Cancel previous render for this page
    if (renderTasksRef.current[pageNum]) {
      try { renderTasksRef.current[pageNum].cancel(); } catch { /* noop */ }
    }

    const page = await doc.getPage(pageNum);
    const viewport = page.getViewport({ scale: targetScale });
    // Cache unscaled dimensions (PDF points) for highlight → FH conversion.
    // Same coord space as FH answer bank regions (top-left origin, points).
    if (!pageDimsRef.current[pageNum]) {
      const v1 = page.getViewport({ scale: 1 });
      pageDimsRef.current[pageNum] = { widthPt: v1.width, heightPt: v1.height };
    }

    // Reset the node contents but preserve the React-portal overlay container
    const overlay = node.querySelector('.pdf-page-overlays');
    node.innerHTML = '';
    node.style.width = `${viewport.width}px`;
    node.style.height = `${viewport.height}px`;
    if (overlay) node.appendChild(overlay);

    // ── Canvas ──
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width * (window.devicePixelRatio || 1));
    canvas.height = Math.floor(viewport.height * (window.devicePixelRatio || 1));
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    canvas.className = 'pdf-page-canvas';
    node.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    const transform = window.devicePixelRatio && window.devicePixelRatio !== 1
      ? [window.devicePixelRatio, 0, 0, window.devicePixelRatio, 0, 0]
      : null;

    // Render annotationMode = ENABLE_FORMS so form widgets are excluded from canvas
    // (they will be drawn interactively in the annotation layer below).
    const renderTask = page.render({
      canvasContext: ctx,
      viewport,
      transform,
      annotationMode: pdfjsLib.AnnotationMode.ENABLE_FORMS,
    });
    renderTasksRef.current[pageNum] = renderTask;
    try {
      await renderTask.promise;
    } catch (err) {
      if (err?.name === 'RenderingCancelledException') return;
      throw err;
    }

    // ── Annotation layer (interactive forms) ──
    const annotations = await page.getAnnotations();
    // Build a map annotationId -> exportValue (buttonValue) for radios/checkboxes.
    // pdfjs 3.4.120 does NOT populate `el.value` on rendered radio inputs, so
    // we stamp the export value onto the DOM ourselves via `data-button-value`.
    const buttonValueById = new Map();
    annotations.forEach(a => {
      if (a && a.id && a.buttonValue !== undefined && a.buttonValue !== null) {
        buttonValueById.set(String(a.id), String(a.buttonValue));
      }
    });
    const annotationLayerDiv = document.createElement('div');
    annotationLayerDiv.className = 'annotationLayer';
    annotationLayerDiv.style.width = `${viewport.width}px`;
    annotationLayerDiv.style.height = `${viewport.height}px`;
    // pdfjs requires --scale-factor CSS var for correct annotation sizing
    annotationLayerDiv.style.setProperty('--scale-factor', String(targetScale));
    node.appendChild(annotationLayerDiv);

    pdfjsLib.AnnotationLayer.render({
      viewport: viewport.clone({ dontFlip: true }),
      div: annotationLayerDiv,
      annotations,
      page,
      linkService: noopLinkService,
      downloadManager: null,
      annotationStorage: doc.annotationStorage,
      renderForms: true,
      enableScripting: false,
      hasJSActions: false,
    });

    // Restore any previous inputs from our answersRef (in case the user already
    // filled the field but we re-rendered due to zoom). We match by field name.
    const currentAnswers = answersRef.current;
    setTimeout(() => {
      // Stamp the real export value ('a'/'b'/'c'/'Yes'/'Off'...) on each
      // radio/checkbox input so save+restore can identify the exact option.
      annotationLayerDiv.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach(el => {
        const eid = el.getAttribute('data-element-id');
        if (eid && buttonValueById.has(eid)) {
          el.setAttribute('data-button-value', buttonValueById.get(eid));
        }
      });

      // Fase 5.1 · Auto-fit visual del contenido dentro de campos de texto.
      // Cuando el alumno/docente escribe más caracteres de los que caben en
      // el ancho original del widget (Tx) o en la altura del textarea
      // multilínea, reducimos gradualmente el `font-size` para que TODO el
      // contenido quede visible dentro del área disponible sin cambiar la
      // posición, tamaño o comportamiento del widget en el PDF. El valor
      // real del campo NUNCA se toca — solo `el.style.fontSize` — así que
      // el autosave (Fase 2), el submit a Feedback Hub y los overrides
      // docente siguen recibiendo exactamente el string escrito por el
      // usuario. Aplica UNICAMENTE a text inputs y textareas; radio,
      // checkbox, select, botones, campos gráficos y overlays de audio
      // conservan el comportamiento previo intacto.
      const _fitTextField = (el) => {
        if (!el) return;
        const isTextInput = el.tagName === 'INPUT'
          && ((el.type || 'text').toLowerCase() === 'text');
        const isTextarea = el.tagName === 'TEXTAREA';
        if (!isTextInput && !isTextarea) return;
        // Cachea la fuente base del render actual la primera vez; los
        // re-renders por zoom crean elementos nuevos, así que la cache
        // vive con el elemento y se descarta con él. El mínimo permite
        // hasta un 25 % de la base (nunca < 4 px) para que cadenas muy
        // largas — p. ej. una frase completa dentro de un campo pequeño —
        // sigan siendo enteramente visibles dentro del área disponible.
        if (!el.dataset.aeBaseFontSize) {
          const cs = window.getComputedStyle(el);
          const baseFs = parseFloat(cs.fontSize) || 12;
          el.dataset.aeBaseFontSize = String(baseFs);
          el.dataset.aeMinFontSize = String(Math.max(baseFs * 0.25, 4));
        }
        const baseFs = parseFloat(el.dataset.aeBaseFontSize);
        const minFs = parseFloat(el.dataset.aeMinFontSize);
        // Vuelve al tamaño original antes de medir por si el usuario ha
        // borrado texto (así el campo puede volver a crecer si sobra).
        el.style.fontSize = `${baseFs}px`;
        const overflows = () => (
          isTextarea
            ? el.scrollHeight > el.clientHeight + 1
            : el.scrollWidth > el.clientWidth + 1
        );
        let fs = baseFs;
        let guard = 60;
        while (fs > minFs && overflows() && guard-- > 0) {
          fs = Math.max(minFs, fs - 0.5);
          el.style.fontSize = `${fs}px`;
        }
      };

      annotationLayerDiv.querySelectorAll('input, textarea, select').forEach(el => {
        const name = el.name || el.getAttribute('data-element-id') || el.id;
        if (!name) return;
        const key = `p${pageNum}::${name}`;
        if (Object.prototype.hasOwnProperty.call(currentAnswers, key)) {
          const val = currentAnswers[key];
          if (el.type === 'radio') {
            // Radios share the same `name`; the stored value is the export
            // value ('a'/'b'/'c') of the selected option → match by buttonValue.
            const bv = el.getAttribute('data-button-value');
            el.checked = bv !== null && String(bv) === String(val);
          } else if (el.type === 'checkbox') {
            el.checked = !!val;
          } else if (el.value !== val) {
            el.value = val ?? '';
          }
        }
        // Fase 2 · If the parent component is showing a read-only review
        // (teacher opening «Ver corrección» or «Ver corrección recibida»),
        // freeze every AcroForm widget so accidental keystrokes never
        // trigger the autosave path (also gated in scheduleAutosave).
        if (readOnlyRef.current) {
          if (el.tagName === 'SELECT' || el.type === 'checkbox' || el.type === 'radio') {
            el.disabled = true;
          } else {
            el.readOnly = true;
          }
        }
        // Fase 5.1 · Enganchar el auto-fit de texto: primero para reflejar
        // el valor restaurado (o el read-only del docente) y después en
        // vivo mientras el usuario escribe.
        _fitTextField(el);
        if (el.tagName === 'INPUT' && (el.type || 'text').toLowerCase() === 'text') {
          el.addEventListener('input', () => _fitTextField(el));
        } else if (el.tagName === 'TEXTAREA') {
          el.addEventListener('input', () => _fitTextField(el));
        }
      });
    }, 0);
  }, []);

  // ── 5. When the PDF & container are ready, create page shells and render them ──
  useEffect(() => {
    if (loading || !containerRef.current || !numPages) return;
    const container = containerRef.current;
    container.innerHTML = '';
    pageRefs.current = {};

    for (let p = 1; p <= numPages; p++) {
      const shell = document.createElement('div');
      shell.className = 'pdf-page-shell';
      shell.setAttribute('data-page', String(p));
      shell.setAttribute('id', `pdf-page-${p}`);
      // Dedicated container for React-portaled overlays (audio buttons, highlights)
      const overlay = document.createElement('div');
      overlay.className = 'pdf-page-overlays';
      overlay.setAttribute('data-overlay-for', String(p));
      shell.appendChild(overlay);
      container.appendChild(shell);
      pageRefs.current[p] = shell;
    }
    seedAnnotationStorage();
    // Render all pages sequentially to avoid worker contention
    (async () => {
      for (let p = 1; p <= numPages; p++) {
        // eslint-disable-next-line no-await-in-loop
        await renderPage(p, scale);
      }
      setOverlaysReady(v => v + 1); // notify portals to mount
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, numPages]);

  // ── 6. Re-render all pages when scale changes ──
  useEffect(() => {
    if (loading || !numPages) return;
    (async () => {
      for (let p = 1; p <= numPages; p++) {
        // eslint-disable-next-line no-await-in-loop
        await renderPage(p, scale);
      }
      setOverlaysReady(v => v + 1); // re-position portaled overlays
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale]);

  // ── 7. Capture form changes via container-level delegation ──
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onInput = (e) => {
      const el = e.target;
      if (!el || !el.matches) return;
      if (!el.matches('input, textarea, select')) return;
      const pageEl = el.closest('.pdf-page-shell');
      if (!pageEl) return;
      const page = parseInt(pageEl.getAttribute('data-page'), 10);
      const name = el.name || el.getAttribute('data-element-id') || el.id;
      if (!name) return;
      const value = el.type === 'radio'
        ? (el.checked ? (el.getAttribute('data-button-value') || el.value) : undefined)
        : (el.type === 'checkbox' ? !!el.checked : el.value);
      if (value === undefined) return; // Unchecked radio: don't overwrite the group's selection
      answersRef.current[`p${page}::${name}`] = value;
      scheduleAutosave();
    };
    container.addEventListener('input', onInput);
    container.addEventListener('change', onInput);
    return () => {
      container.removeEventListener('input', onInput);
      container.removeEventListener('change', onInput);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numPages, submitted]);

  const scheduleAutosave = useCallback(() => {
    if (submitted) return;
    // Fase 2 · in correction mode, edits belong to the manual-correction
    // stream, not to the draft. Never write them back to /activities/draft
    // (that would pollute the original submission's cached answers).
    if (correctionMode) return;
    // Teacher's readOnly review (or any view with a `review` payload
    // present but not in correctionMode) must not autosave either — the
    // draft belongs to the student, not to whoever is reading the review.
    if (readOnly || review) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSaveState('saving');
    debounceRef.current = setTimeout(async () => {
      try {
        const arr = Object.entries(answersRef.current).map(([k, value]) => {
          const [pageStr, name] = k.split('::');
          return { page: parseInt(pageStr.slice(1), 10), name, value };
        });
        await axios.post(`${API}/activities/draft`, { content_id: contentId, answers: arr }, { headers });
        setSaveState('saved');
      } catch {
        setSaveState('error');
      }
    }, 500);
  }, [contentId, submitted, correctionMode, readOnly, review]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 8. Track visible page (for the page # indicator) ──
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !numPages) return;
    const onScroll = () => {
      // Find the page whose top is closest to (but not past) the viewport center
      const containerRect = container.getBoundingClientRect();
      const center = containerRect.top + containerRect.height / 2;
      let best = 1;
      let bestDist = Infinity;
      for (let p = 1; p <= numPages; p++) {
        const node = pageRefs.current[p];
        if (!node) continue;
        const r = node.getBoundingClientRect();
        const pc = r.top + r.height / 2;
        const d = Math.abs(pc - center);
        if (d < bestDist) { bestDist = d; best = p; }
      }
      setCurrentPage(best);
      setPageInput(String(best));
    };
    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [numPages]);

  // ── Toolbar handlers ──
  const goToPage = (p) => {
    const n = Math.max(1, Math.min(numPages, p));
    const node = pageRefs.current[n];
    if (node && containerRef.current) {
      node.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  };
  const zoomIn = () => setScale(s => Math.min(MAX_SCALE, +(s + 0.15).toFixed(2)));
  const zoomOut = () => setScale(s => Math.max(MIN_SCALE, +(s - 0.15).toFixed(2)));

  // ── Audio player ──
  const openAudio = async (audio) => {
    if (!audio) return;
    if (audio.has_bytes === false) {
      toast.warning('Este audio no se pudo extraer del PDF (formato no soportado).');
      return;
    }
    // Stop any previous track
    if (audioElRef.current) {
      try { audioElRef.current.pause(); } catch { /* noop */ }
    }
    if (activeAudio?.url) {
      try { URL.revokeObjectURL(activeAudio.url); } catch { /* noop */ }
    }
    try {
      const res = await axios.get(`${API}/activities/content/${contentId}/audio/${audio.index}`, {
        headers, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      setActiveAudio({ index: audio.index, url, label: audio.label || `Audio ${audio.index + 1}` });
      setAudioPlaying(true);
    } catch (err) {
      toast.error('No se pudo reproducir el audio');
    }
  };
  const toggleAudio = () => {
    const el = audioElRef.current;
    if (!el) return;
    if (el.paused) { el.play(); setAudioPlaying(true); }
    else { el.pause(); setAudioPlaying(false); }
  };
  const closeAudio = () => {
    if (audioElRef.current) { try { audioElRef.current.pause(); } catch { /* noop */ } }
    if (activeAudio?.url) { try { URL.revokeObjectURL(activeAudio.url); } catch { /* noop */ } }
    setActiveAudio(null);
    setAudioPlaying(false);
  };
  useEffect(() => {
    // Autoplay when a new track is set (respects browser autoplay policy — user just clicked)
    const el = audioElRef.current;
    if (el && activeAudio?.url) {
      el.src = activeAudio.url;
      el.play().catch(() => { setAudioPlaying(false); });
    }
  }, [activeAudio]);

  // ── Highlighter (session-only) ──
  const rebuildHighlights = () => setHighlightsVersion(v => v + 1);
  const addRectHighlight = (pageNum, rect) => {
    const shell = pageRefs.current[pageNum];
    if (!shell) return;
    const w = shell.offsetWidth || 1;
    const h = shell.offsetHeight || 1;
    // iter70 · If the teacher is drawing in review mode, the rect is a
    // NEW visual highlight → persist it via the backend so students and
    // future teachers see it too. The stored coordinates must be in PDF
    // points (matching visual_highlights[].rect), so we translate using
    // the cached page dimensions.
    if (canManageVisualHighlights) {
      const dims = pageDimsRef.current[pageNum];
      const widthPt = dims?.widthPt || 0;
      const heightPt = dims?.heightPt || 0;
      if (widthPt && heightPt) {
        const x0 = (rect.left / w) * widthPt;
        const y0 = (rect.top / h) * heightPt;
        const x1 = ((rect.left + rect.width) / w) * widthPt;
        const y1 = ((rect.top + rect.height) / h) * heightPt;
        postVisualHighlightAction('add', {
          page: pageNum - 1, // back to 0-based to match the FH contract
          rect: [x0, y0, x1, y1],
          color: highlightColor,
          kind: 'rect',
        });
        return;
      }
    }
    const hl = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      kind: 'rect',
      color: highlightColor,
      xPct: (rect.left / w) * 100,
      yPct: (rect.top / h) * 100,
      wPct: (rect.width / w) * 100,
      hPct: (rect.height / h) * 100,
    };
    highlightsRef.current[pageNum] = [...(highlightsRef.current[pageNum] || []), hl];
    rebuildHighlights();
  };
  const addFreeHighlight = (pageNum, points) => {
    const shell = pageRefs.current[pageNum];
    if (!shell || points.length < 2) return;
    const w = shell.offsetWidth || 1;
    const h = shell.offsetHeight || 1;
    const hl = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      kind: 'free',
      color: highlightColor,
      // Store thickness as a % of the page width so it scales with zoom
      thicknessPct: (highlightThickness / w) * 100,
      pointsPct: points.map(([x, y]) => [(x / w) * 100, (y / h) * 100]),
    };
    highlightsRef.current[pageNum] = [...(highlightsRef.current[pageNum] || []), hl];
    rebuildHighlights();
  };
  const removeHighlight = (pageNum, id) => {
    highlightsRef.current[pageNum] = (highlightsRef.current[pageNum] || []).filter(h => h.id !== id);
    rebuildHighlights();
  };
  const clearHighlights = () => {
    highlightsRef.current = {};
    rebuildHighlights();
  };

  // Close the color/mode picker when the user clicks anywhere outside the
  // popover AND outside the highlighter button. Uses a document-level listener
  // instead of an overlay div so we do NOT swallow the user's next gesture
  // (drawing a stroke or hitting a form field remains fully functional).
  useEffect(() => {
    if (!highlightPickerOpen) return;
    const onDocDown = (e) => {
      const container = highlightPickerContainerRef.current;
      if (container && container.contains(e.target)) return; // inside button or picker
      setHighlightPickerOpen(false);
    };
    document.addEventListener('mousedown', onDocDown, true);
    return () => document.removeEventListener('mousedown', onDocDown, true);
  }, [highlightPickerOpen]);

  // Escape while the highlighter is on deactivates the tool instead of
  // bubbling up and closing the whole activity dialog.
  useEffect(() => {
    if (!highlighterActive) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setHighlighterActive(false);
        setHighlightPickerOpen(false);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [highlighterActive]);

  // Highlighter mouse handlers attached to each page overlay
  const onHighlightMouseDown = (pageNum, e) => {
    if (!highlighterActive) return;
    if (e.button !== 0) return;
    if (e.target.closest('.pdf-highlight-chip, .pdf-highlight-path')) return;
    const rect = pageRefs.current[pageNum].getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (highlightMode === 'rect') {
      drawingRef.current = { kind: 'rect', pageNum, startX: x, startY: y, currentX: x, currentY: y };
    } else {
      drawingRef.current = { kind: 'free', pageNum, points: [[x, y]] };
    }
    rebuildHighlights();
    e.preventDefault();
  };
  useEffect(() => {
    if (!highlighterActive) return;
    const onMove = (e) => {
      const d = drawingRef.current;
      if (!d) return;
      const rect = pageRefs.current[d.pageNum]?.getBoundingClientRect();
      if (!rect) return;
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (d.kind === 'rect') {
        d.currentX = x; d.currentY = y;
      } else {
        // Only append if far enough from the last point to keep it smooth
        const last = d.points[d.points.length - 1];
        if (Math.hypot(x - last[0], y - last[1]) >= 2) d.points.push([x, y]);
      }
      rebuildHighlights();
    };
    const onUp = () => {
      const d = drawingRef.current;
      if (!d) return;
      if (d.kind === 'rect') {
        const left = Math.min(d.startX, d.currentX);
        const top = Math.min(d.startY, d.currentY);
        const width = Math.abs(d.currentX - d.startX);
        const height = Math.abs(d.currentY - d.startY);
        if (width > 4 && height > 4) addRectHighlight(d.pageNum, { left, top, width, height });
      } else if (d.kind === 'free' && d.points.length > 1) {
        addFreeHighlight(d.pageNum, d.points);
      }
      drawingRef.current = null;
      rebuildHighlights();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlighterActive, highlightMode, highlightColor, highlightThickness]);

  // Compute the live drawing preview
  const liveDraw = useMemo(() => {
    const d = drawingRef.current;
    if (!d) return null;
    if (d.kind === 'rect') {
      return {
        kind: 'rect',
        pageNum: d.pageNum,
        left: Math.min(d.startX, d.currentX),
        top: Math.min(d.startY, d.currentY),
        width: Math.abs(d.currentX - d.startX),
        height: Math.abs(d.currentY - d.startY),
      };
    }
    return { kind: 'free', pageNum: d.pageNum, points: d.points };
  }, [highlightsVersion]);


  const submitActivity = async () => {
    if (submitted) return;
    setSubmitting(true);
    try {
      const arr = Object.entries(answersRef.current).map(([k, value]) => {
        const [pageStr, name] = k.split('::');
        return { page: parseInt(pageStr.slice(1), 10), name, value };
      });
      // Fase 2 · Convert the student's highlighter marks (stored as
      // display-pixel percentages) into PDF-point rectangles with
      // top-left origin — the SAME coordinate space used by the FH
      // answer bank regions. Without this the graphic activities
      // (Tipo B) are graded as «sin responder».
      //   - `rect` highlights → straight rect in points.
      //   - `free` highlights → bounding box of the polyline in points.
      // `page` is emitted as 0-based (FH convention); the backend does
      // NOT re-normalize `highlights[].page` (only `answers[].page`).
      const fhHighlights = [];
      Object.entries(highlightsRef.current || {}).forEach(([pageNumStr, list]) => {
        const pageNum1 = parseInt(pageNumStr, 10);
        const dims = pageDimsRef.current[pageNum1];
        if (!dims || !Array.isArray(list) || !list.length) return;
        const { widthPt, heightPt } = dims;
        list.forEach(h => {
          let x0, y0, x1, y1;
          let pointsPt = null;
          if (h.kind === 'rect') {
            x0 = (h.xPct / 100) * widthPt;
            y0 = (h.yPct / 100) * heightPt;
            x1 = x0 + (h.wPct / 100) * widthPt;
            y1 = y0 + (h.hPct / 100) * heightPt;
          } else if (h.kind === 'free' && Array.isArray(h.pointsPct) && h.pointsPct.length) {
            const xs = h.pointsPct.map(p => (p[0] / 100) * widthPt);
            const ys = h.pointsPct.map(p => (p[1] / 100) * heightPt);
            x0 = Math.min(...xs); x1 = Math.max(...xs);
            y0 = Math.min(...ys); y1 = Math.max(...ys);
            // Expand the bounding box by the stroke thickness so single-
            // stroke marks don't collapse to a zero-area rect.
            const thicknessPt = ((h.thicknessPct || 0) / 100) * widthPt / 2;
            x0 -= thicknessPt; x1 += thicknessPt;
            y0 -= thicknessPt; y1 += thicknessPt;
            // iter71.4 · Also send the exact polyline (not just its bbox)
            // so a visual_highlight (FIND / crossword trace) survives as
            // the real path the alumno drew, instead of a rectangle that
            // covers every letter inside its bounding box.
            pointsPt = xs.map((x, i) => [x, ys[i]]);
          } else {
            return;
          }
          fhHighlights.push({
            page: pageNum1 - 1,
            rect: [x0, y0, x1, y1],
            // iter71.3 · Preserve the stroke's colour/tool end-to-end so
            // visual_highlights persist with the exact colour the alumno
            // drew (amarillo, rosa, verde…), not a green default.
            color: h.color,
            kind: h.kind,
            thickness_pct: h.thicknessPct,
            points: pointsPt,
          });
        });
      });
      // Fase 3 iter64 · Include AE-detected audio_exercises in the FH
      // payload. Feedback Hub (updated by Support after the iter63
      // diagnostic) now persists our IDs as the single source of truth
      // when this array has at least one element and skips its own ORB
      // detection. This gives us a single exercise_id end-to-end:
      //   mic_detection → recorder overlay → preSubmitBlob key →
      //   /submit-activity → FH → /student-audio → /review → teacher-audio.
      const fhAudioExercises = (previewAudioExercises || []).map(ex => ({
        exercise_id: ex.exercise_id,
        page: ex.page,
        rect: ex.rect,
        x: ex.x,
        y: ex.y,
        width: ex.width,
        height: ex.height,
      }));
      const res = await axios.post(
        `${API}/activities/submit`,
        { content_id: contentId, answers: arr, highlights: fhHighlights, audio_exercises: fhAudioExercises },
        { headers },
      );
      setSubmitted(res.data);
      const ae = res.data.audio_exercises || [];
      const activityId = res.data.activity_id;
      const preBlobs = preSubmitBlobs; // snapshot before we reset

      // Fase 3 iter64 · upload every local pre-submit recording using its
      // AE exercise_id directly. Because AE now sends the audio_exercises
      // in /submit-activity, FH holds the same IDs and /student-audio
      // accepts them 1:1. No geometric mapper, no ±1 page tolerance, no
      // IoU threshold, no fallback: the ID that keyed preSubmitBlobs is
      // the ID that FH persists.
      const preExerciseIds = Object.keys(preBlobs);
      let okCount = 0;
      const failedIds = [];
      if (activityId && preExerciseIds.length > 0) {
        setPreSubmitUploadRemaining(preExerciseIds.length);
        for (const exId of preExerciseIds) {
          const info = preBlobs[exId];
          if (!info || !info.blob) { setPreSubmitUploadRemaining(v => Math.max(0, v - 1)); continue; }
          try {
            const fd = new FormData();
            fd.append('file', new File([info.blob], info.filename || `${exId}.webm`, { type: info.mime || 'audio/webm' }));
            fd.append('exercise_id', exId);
            const uploadRes = await axios.post(
              `${API}/activities/activity/${activityId}/student-audio`,
              fd,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            const uploaded = uploadRes.data || {};
            if (uploaded.audio_id && uploaded.exercise_id) {
              setPostSubmitAudio(prev => {
                if (!prev || !Array.isArray(prev.audio_exercises)) return prev;
                const patched = prev.audio_exercises.map(ex => (
                  ex.exercise_id === uploaded.exercise_id
                    ? { ...ex, student_audio: { audio_id: uploaded.audio_id, attempt: uploaded.attempt } }
                    : ex
                ));
                return { ...prev, audio_exercises: patched };
              });
              const idx = (ae || []).findIndex(x => x.exercise_id === uploaded.exercise_id);
              if (idx >= 0) ae[idx] = { ...ae[idx], student_audio: { audio_id: uploaded.audio_id, attempt: uploaded.attempt } };
              okCount += 1;
            }
          } catch (e) {
            failedIds.push(exId);
            toast.error(`No se pudo subir el audio ${exId}: ${e?.response?.data?.detail || e.message}`);
          } finally {
            setPreSubmitUploadRemaining(v => Math.max(0, v - 1));
          }
        }
        // Preserve blobs that failed to upload so the student can retry.
        // Successful ones are now persisted in FH and no longer needed
        // locally.
        setPreSubmitBlobs(prev => {
          const next = {};
          for (const id of failedIds) if (prev[id]) next[id] = prev[id];
          return next;
        });
      }

      if (ae.length > 0) {
        setPostSubmitAudio({ activity_id: activityId, audio_exercises: ae });
      }
      const totalTried = preExerciseIds.length;
      if (totalTried === 0) {
        if (ae.length > 0) {
          toast.success(`Actividad entregada (v${res.data.version}). ${ae.length} ejercicio(s) de audio · graba tu voz cuando quieras.`);
        } else {
          toast.success(`Actividad entregada (v${res.data.version}).`);
        }
      } else if (okCount === totalTried) {
        toast.success(`Actividad entregada (v${res.data.version}). ${okCount} audio(s) enviado(s).`);
      } else {
        toast.warning(
          `Actividad entregada (v${res.data.version}). ${okCount}/${totalTried} audio(s) enviado(s) · ${failedIds.length} con error de subida.`,
          { duration: 8000 },
        );
      }
      onSubmitted?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al entregar la actividad');
    }
    setSubmitting(false);
  };

  // iter74 · Audio gate. When the activity contains mic icons and the
  // student has not saved every recording yet, «Entregar actividad» stays
  // visually disabled and clicking it shows a blocking notice. Only active
  // for Lecciones / Tareas / Refuerzo Académico (`requireAudioBeforeSubmit`).
  const [audioGateNotice, setAudioGateNotice] = useState(false);
  // iter75 · «X de N grabaciones guardadas» shown in the viewer top strip.
  const audioTotalCount = (previewAudioExercises || []).length;
  const audioSavedCount = (previewAudioExercises || [])
    .filter(a => !!preSubmitBlobs[a.exercise_id]).length;
  const audioGateBlocked = useMemo(() => {
    if (!requireAudioBeforeSubmit) return false;
    // Still fetching the mic coordinates: block until we know whether this
    // activity needs a recording (otherwise a fast click skips the gate).
    if (shouldFetchPreviewAudio && previewAudioExercises === null) return true;
    if (audioMode !== 'student-pre-submit') return false;
    const list = previewAudioExercises || [];
    if (list.length === 0) return false;
    return list.some(a => !preSubmitBlobs[a.exercise_id]);
  }, [requireAudioBeforeSubmit, shouldFetchPreviewAudio, audioMode, previewAudioExercises, preSubmitBlobs]);

  // Fase 2 · Manual correction submit (student, 2nd pass).
  // Posts to /manual-correction/{activity_id} which stores answers in AE
  // ONLY. No call to Feedback Hub is triggered by this button.
  const [correctionSent, setCorrectionSent] = useState(false);
  const submitManualCorrection = async () => {
    if (!correctionActivityId) {
      toast.error('Falta el identificador de la actividad para enviar la corrección.');
      return;
    }
    setSubmitting(true);
    try {
      const arr = Object.entries(answersRef.current).map(([k, value]) => {
        const [pageStr, name] = k.split('::');
        return { page: parseInt(pageStr.slice(1), 10), name, value };
      });
      const res = await axios.post(
        `${API}/activities/activity/${correctionActivityId}/manual-correction`,
        { answers: arr },
        { headers },
      );
      setCorrectionSent(true);
      toast.success('Corrección enviada al docente.');
      onSubmitted?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar la corrección');
    }
    setSubmitting(false);
  };

  // Fase 2 · Apply / revert a teacher manual override on one field.
  // POST /activity/{id}/field-override (or DELETE for revert). Never calls
  // Feedback Hub. Parent notifies via onOverrideChanged so the review
  // payload is refetched and the overlays repaint.
  const applyOverride = async (field, newStatus) => {
    if (!overrideActivityId) return;
    try {
      if (newStatus === null) {
        await axios.delete(
          `${API}/activities/activity/${overrideActivityId}/field-override`,
          { headers, params: { page: (field.page ?? 0) + 1, name: field.name } },
        );
      } else {
        await axios.post(
          `${API}/activities/activity/${overrideActivityId}/field-override`,
          { page: (field.page ?? 0) + 1, name: field.name, status: newStatus },
          { headers },
        );
      }
      setOpenCommentId(null);
      onOverrideChanged?.();
      toast.success(newStatus === null ? 'Estado revertido a Feedback Hub' : 'Estado actualizado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo aplicar el cambio');
    }
  };

  // Fase 2 · Apply / revert a teacher manual override on a GRAPHIC answer
  // (Tipo B / CROSS OUT). Uses the parallel `/graphic-override` endpoint —
  // keyed by (page, question) instead of (page, name). Same invariants as
  // the AcroForm override above: no Feedback Hub round-trip, parent
  // refetches the review payload via onOverrideChanged.
  const applyGraphicOverride = async (result, newStatus) => {
    if (!overrideActivityId) return;
    try {
      if (newStatus === null) {
        await axios.delete(
          `${API}/activities/activity/${overrideActivityId}/graphic-override`,
          { headers, params: { page: result.page ?? 0, question: result.question } },
        );
      } else {
        await axios.post(
          `${API}/activities/activity/${overrideActivityId}/graphic-override`,
          { page: result.page ?? 0, question: result.question, status: newStatus },
          { headers },
        );
      }
      setOpenCommentId(null);
      onOverrideChanged?.();
      toast.success(newStatus === null ? 'Estado revertido a Feedback Hub' : 'Estado actualizado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo aplicar el cambio');
    }
  };

  const containerClass = darkMode
    ? 'flex-1 overflow-auto bg-slate-900'
    : 'flex-1 overflow-auto bg-slate-100';
  const scrollContainerStyle = scrollMode === 'horizontal'
    ? { display: 'flex', flexDirection: 'row', gap: 16, padding: 16 }
    : scrollMode === 'wrapped'
    ? { display: 'flex', flexWrap: 'wrap', gap: 16, padding: 16, justifyContent: 'center' }
    : { display: 'flex', flexDirection: 'column', gap: 16, padding: 16, alignItems: 'center' };
  const bodyCursor = tool === 'hand' ? 'grab' : 'auto';

  return (
    <div data-testid="pdf-activity-viewer" className="relative flex flex-col h-[85vh] border rounded-xl overflow-hidden bg-white">
      {audioGateNotice && (
        <div
          data-testid="audio-gate-notice"
          className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-6"
          onClick={() => setAudioGateNotice(false)}
        >
          <div
            className="max-w-xl w-full bg-white rounded-2xl shadow-2xl border-4 border-amber-400 p-8 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-extrabold text-amber-600 mb-4">⚠️ Grabación pendiente</h3>
            <p className="text-base text-slate-800 font-semibold mb-2">
              No puedes entregar esta actividad todavía.
            </p>
            <p className="text-base text-slate-700">
              Esta actividad incluye una grabación de audio obligatoria. Pulsa el botón Grabar,
              completa la grabación y guárdala antes de entregar.
            </p>
            <Button
              data-testid="audio-gate-notice-close"
              onClick={() => setAudioGateNotice(false)}
              className="mt-6 rounded-full bg-amber-500 hover:bg-amber-600"
            >
              Entendido
            </Button>
          </div>
        </div>
      )}
      {/* Top strip: Guardado indicator + Entregar / Enviar corrección */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50">
        <div className="flex items-center gap-2 text-sm">
          {correctionMode ? (
            correctionSent ? (
              <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                <CheckCircle className="w-4 h-4" /> Corrección enviada al docente
              </span>
            ) : (
              <span data-testid="save-state" className={
                saveState === 'saving' ? 'text-amber-600 inline-flex items-center gap-1' :
                saveState === 'saved' ? 'text-emerald-700 inline-flex items-center gap-1' :
                saveState === 'error' ? 'text-red-700 inline-flex items-center gap-1' :
                'text-slate-500 inline-flex items-center gap-1'
              }>
                <Save className="w-4 h-4" />
                Modo corrección · edita tus respuestas y pulsa «Enviar corrección»
              </span>
            )
          ) : submitted ? (
            <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
              <CheckCircle className="w-4 h-4" /> Entregada · v{submitted.version || 1}
            </span>
          ) : (
            <span data-testid="save-state" className={
              saveState === 'saving' ? 'text-amber-600 inline-flex items-center gap-1' :
              saveState === 'saved' ? 'text-emerald-700 inline-flex items-center gap-1' :
              saveState === 'error' ? 'text-red-700 inline-flex items-center gap-1' :
              'text-slate-500 inline-flex items-center gap-1'
            }>
              <Save className="w-4 h-4" />
              {saveState === 'saving' ? 'Guardando…' : saveState === 'saved' ? 'Guardado' : saveState === 'error' ? 'Error de sincronización' : ' '}
            </span>
          )}
          {audioMode === 'student-pre-submit' && audioTotalCount > 0 && (
            <span
              data-testid="audio-save-counter"
              className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${
                audioSavedCount === audioTotalCount
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-amber-100 text-amber-700'
              }`}
              title="Grabaciones de audio guardadas en esta actividad"
            >
              <Mic className="w-3 h-3" />
              {audioSavedCount} de {audioTotalCount} grabaciones guardadas
            </span>
          )}
        </div>
        {correctionMode ? (
          <Button
            data-testid="submit-correction-btn"
            onClick={submitManualCorrection}
            disabled={submitting || correctionSent}
            className="gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700"
          >
            <Send className="w-4 h-4" /> {submitting ? 'Enviando…' : correctionSent ? 'Reenviar corrección' : 'Enviar corrección'}
          </Button>
        ) : (
          <Button
            data-testid="submit-activity-btn"
            onClick={() => { if (audioGateBlocked) { setAudioGateNotice(true); return; } submitActivity(); }}
            aria-disabled={audioGateBlocked || undefined}
            disabled={submitting || !!submitted || readOnly || Boolean(review)}
            className={`gap-1 rounded-full bg-sky-600 hover:bg-sky-700 ${audioGateBlocked ? 'opacity-50 cursor-not-allowed hover:bg-sky-600' : ''}`}
            title={audioGateBlocked ? 'Grabación pendiente: graba y guarda el audio antes de entregar' : undefined}
            style={(readOnly || Boolean(review)) ? { display: 'none' } : undefined}
          >
            <Send className="w-4 h-4" /> {preSubmitUploadRemaining > 0 ? `Subiendo audios… (${preSubmitUploadRemaining})` : submitting ? 'Entregando…' : submitted ? 'Entregada' : 'Entregar actividad'}
          </Button>
        )}
        {reviewSummary && (
          <div
            data-testid="review-summary"
            className="ml-3 inline-flex items-center gap-2 text-xs px-2 py-1 rounded-full bg-slate-100"
            title="Resumen de la corrección de Feedback Hub"
          >
            <span className="text-emerald-700 font-semibold">✓ {reviewSummary.correct ?? 0}</span>
            <span className="text-red-700 font-semibold">✗ {reviewSummary.incorrect ?? 0}</span>
            <span className="text-amber-700 font-semibold">~ {reviewSummary.partial ?? 0}</span>
            <span className="text-slate-500 font-semibold">· {reviewSummary.unanswered ?? 0}</span>
            <span className="text-slate-400">/ {reviewSummary.total ?? 0}</span>
          </div>
        )}
        {graphicSummary && (graphicSummary.total ?? 0) > 0 && (
          <div
            data-testid="review-graphic-summary"
            className="ml-2 inline-flex items-center gap-2 text-xs px-2 py-1 rounded-full bg-indigo-50 border border-indigo-100"
            title="Resumen de respuestas gráficas (CROSS OUT) de Feedback Hub"
          >
            <span className="text-indigo-700 font-semibold uppercase tracking-wide" style={{ fontSize: 10 }}>Gráficas</span>
            <span className="text-emerald-700 font-semibold">✓ {graphicSummary.correct ?? 0}</span>
            <span className="text-red-700 font-semibold">✗ {graphicSummary.incorrect ?? 0}</span>
            <span className="text-slate-500 font-semibold">· {graphicSummary.unanswered ?? 0}</span>
            <span className="text-slate-400">/ {graphicSummary.total ?? 0}</span>
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-1 px-3 py-1.5 border-b bg-white text-slate-700">
        {/* Left group: search + thumbnails + page nav */}
        <button
          type="button"
          data-testid="toolbar-search-btn"
          title="Buscar"
          onClick={() => setShowSearch(v => !v)}
          className="p-1.5 rounded hover:bg-slate-100"
        >
          <Search className="w-4 h-4" />
        </button>
        <button
          type="button"
          data-testid="toolbar-thumbs-btn"
          title="Miniaturas"
          onClick={() => setShowThumbs(v => !v)}
          className={`p-1.5 rounded hover:bg-slate-100 ${showThumbs ? 'bg-slate-200' : ''}`}
        >
          <LayoutGrid className="w-4 h-4" />
        </button>
        <button
          type="button"
          data-testid="toolbar-prev-page"
          title="Anterior"
          onClick={() => goToPage(currentPage - 1)}
          className="p-1.5 rounded hover:bg-slate-100"
          disabled={currentPage <= 1}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <form
          onSubmit={(e) => { e.preventDefault(); const n = parseInt(pageInput, 10); if (!Number.isNaN(n)) goToPage(n); }}
          className="flex items-center gap-1 text-xs"
        >
          <input
            type="text"
            data-testid="toolbar-page-input"
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value.replace(/[^0-9]/g, ''))}
            onBlur={(e) => { const n = parseInt(e.target.value, 10); if (!Number.isNaN(n)) goToPage(n); }}
            className="w-10 text-center border rounded px-1 py-0.5"
          />
          <span className="text-slate-500">/ {numPages || 0}</span>
        </form>
        <button
          type="button"
          data-testid="toolbar-next-page"
          title="Siguiente"
          onClick={() => goToPage(currentPage + 1)}
          className="p-1.5 rounded hover:bg-slate-100"
          disabled={currentPage >= numPages}
        >
          <ChevronRight className="w-4 h-4" />
        </button>

        {/* Center group: zoom */}
        <div className="flex-1 flex items-center justify-center gap-1">
          <button
            type="button"
            data-testid="toolbar-zoom-out"
            title="Alejar"
            onClick={zoomOut}
            className="p-1.5 rounded hover:bg-slate-100"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span data-testid="toolbar-zoom-level" className="text-xs w-14 text-center tabular-nums">
            {Math.round(scale * 100)}%
          </span>
          <button
            type="button"
            data-testid="toolbar-zoom-in"
            title="Acercar"
            onClick={zoomIn}
            className="p-1.5 rounded hover:bg-slate-100"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
        </div>

        {/* Right group: highlighter + theme + tool + more */}
        <div className="relative" ref={highlightPickerContainerRef}>
          <button
            type="button"
            ref={highlightBtnRef}
            data-testid="toolbar-highlighter-btn"
            data-highlighter-mode={canManageVisualHighlights ? 'visual-review' : 'session'}
            title={
              canManageVisualHighlights
                ? (highlighterActive
                    ? 'Resaltados visuales del docente (dibuja para añadir, click sobre uno existente para eliminar)'
                    : 'Añadir resaltados visuales (FIND / anotaciones libres)')
                : (highlighterActive ? 'Configurar resaltador (click de nuevo para desactivar)' : 'Resaltador')
            }
            onClick={() => {
              if (highlighterActive) {
                // If already active: toggle only the picker; deactivate on 2nd click when picker was open.
                if (highlightPickerOpen) {
                  setHighlighterActive(false);
                  setHighlightPickerOpen(false);
                } else {
                  setHighlightPickerOpen(true);
                }
              } else {
                setHighlighterActive(true);
                setHighlightPickerOpen(true);
              }
            }}
            className={`p-1.5 rounded hover:bg-slate-100 ${highlighterActive ? 'bg-yellow-100 text-yellow-900 ring-1 ring-yellow-400' : ''}`}
            style={highlighterActive ? { boxShadow: `inset 0 -3px 0 0 ${highlightColor}` } : undefined}
          >
            <Highlighter className="w-4 h-4" />
          </button>
          {highlighterActive && highlightPickerOpen && (
            <div
              data-testid="toolbar-highlighter-picker"
              className="absolute top-full mt-1 right-0 z-40 w-56 rounded-lg border bg-white shadow-lg p-3 text-xs"
            >
              {/* Mode */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600 font-medium">Modo</span>
                <div className="inline-flex rounded-md border overflow-hidden">
                  <button
                    type="button"
                    data-testid="highlighter-mode-rect"
                    onClick={() => setHighlightMode('rect')}
                    className={`px-2 py-1 ${highlightMode === 'rect' ? 'bg-slate-900 text-white' : 'hover:bg-slate-100'}`}
                  >
                    Rectángulo
                  </button>
                  <button
                    type="button"
                    data-testid="highlighter-mode-free"
                    onClick={() => setHighlightMode('free')}
                    className={`px-2 py-1 ${highlightMode === 'free' ? 'bg-slate-900 text-white' : 'hover:bg-slate-100'}`}
                  >
                    Libre
                  </button>
                </div>
              </div>
              {/* Colors */}
              <div className="mb-2">
                <span className="text-slate-600 font-medium block mb-1">Color</span>
                <div className="flex items-center gap-1.5">
                  {[
                    { c: '#facc15', name: 'Amarillo' },
                    { c: '#4ade80', name: 'Verde' },
                    { c: '#f472b6', name: 'Rosa' },
                    { c: '#60a5fa', name: 'Azul' },
                    { c: '#fb923c', name: 'Naranja' },
                  ].map(({ c, name }) => (
                    <button
                      key={c}
                      type="button"
                      data-testid={`highlighter-color-${name.toLowerCase()}`}
                      onClick={() => setHighlightColor(c)}
                      title={name}
                      className={`w-6 h-6 rounded-full border-2 ${highlightColor === c ? 'border-slate-900' : 'border-white ring-1 ring-slate-300'}`}
                      style={{ background: c }}
                    />
                  ))}
                </div>
              </div>
              {/* Thickness (free mode only) */}
              {highlightMode === 'free' && (
                <div className="mb-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-slate-600 font-medium">Grosor</span>
                    <span className="text-slate-500 tabular-nums">{highlightThickness}px</span>
                  </div>
                  <input
                    type="range"
                    min={4} max={28} step={1}
                    value={highlightThickness}
                    onChange={(e) => setHighlightThickness(parseInt(e.target.value, 10))}
                    data-testid="highlighter-thickness"
                    className="w-full"
                  />
                </div>
              )}
            </div>
          )}
        </div>
        {Object.values(highlightsRef.current).some(a => a.length) && (
          <button
            type="button"
            data-testid="toolbar-highlighter-clear"
            title="Borrar todos los resaltados"
            onClick={clearHighlights}
            className="p-1.5 rounded hover:bg-red-50 text-red-500"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          data-testid="toolbar-theme-btn"
          title="Cambiar tema"
          onClick={() => setDarkMode(v => !v)}
          className="p-1.5 rounded hover:bg-slate-100"
        >
          {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
        <button
          type="button"
          data-testid="toolbar-tool-btn"
          title={tool === 'hand' ? 'Herramienta de mano' : 'Selección de texto'}
          onClick={() => setTool(t => t === 'hand' ? 'select' : 'hand')}
          className={`p-1.5 rounded hover:bg-slate-100 ${tool === 'hand' ? 'bg-slate-200' : ''}`}
        >
          {tool === 'hand' ? <Hand className="w-4 h-4" /> : <MousePointer2 className="w-4 h-4" />}
        </button>
        <div className="relative">
          <button
            type="button"
            data-testid="toolbar-more-btn"
            title="Más opciones"
            onClick={() => setMoreOpen(v => !v)}
            className={`p-1.5 rounded hover:bg-slate-100 ${moreOpen ? 'bg-slate-200' : ''}`}
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {moreOpen && (
            <div
              data-testid="toolbar-more-menu"
              className="absolute right-0 top-full mt-1 z-30 w-56 bg-white border rounded-md shadow-lg py-1 text-sm"
              onMouseLeave={() => setMoreOpen(false)}
            >
              <button
                type="button"
                data-testid="more-last-page"
                onClick={() => { goToPage(numPages); setMoreOpen(false); }}
                className="w-full text-left px-3 py-1.5 hover:bg-slate-100"
              >
                Última página
              </button>
              <div className="border-t my-1" />
              <button
                type="button"
                data-testid="more-tool-select"
                onClick={() => { setTool('select'); setMoreOpen(false); }}
                className={`w-full text-left px-3 py-1.5 hover:bg-slate-100 ${tool === 'select' ? 'font-medium' : ''}`}
              >
                Selección de texto {tool === 'select' && '✓'}
              </button>
              <button
                type="button"
                data-testid="more-tool-hand"
                onClick={() => { setTool('hand'); setMoreOpen(false); }}
                className={`w-full text-left px-3 py-1.5 hover:bg-slate-100 ${tool === 'hand' ? 'font-medium' : ''}`}
              >
                Herramienta de mano {tool === 'hand' && '✓'}
              </button>
              <div className="border-t my-1" />
              <button
                type="button"
                data-testid="more-scroll-vertical"
                onClick={() => { setScrollMode('vertical'); setMoreOpen(false); }}
                className={`w-full text-left px-3 py-1.5 hover:bg-slate-100 ${scrollMode === 'vertical' ? 'font-medium' : ''}`}
              >
                Página desplazable {scrollMode === 'vertical' && '✓'}
              </button>
              <button
                type="button"
                data-testid="more-scroll-horizontal"
                onClick={() => { setScrollMode('horizontal'); setMoreOpen(false); }}
                className={`w-full text-left px-3 py-1.5 hover:bg-slate-100 ${scrollMode === 'horizontal' ? 'font-medium' : ''}`}
              >
                Desplazamiento horizontal {scrollMode === 'horizontal' && '✓'}
              </button>
              <button
                type="button"
                data-testid="more-scroll-wrapped"
                onClick={() => { setScrollMode('wrapped'); setMoreOpen(false); }}
                className={`w-full text-left px-3 py-1.5 hover:bg-slate-100 ${scrollMode === 'wrapped' ? 'font-medium' : ''}`}
              >
                Envoltorio {scrollMode === 'wrapped' && '✓'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Search bar (simple: just Ctrl+F equivalent placeholder for now) */}
      {showSearch && (
        <div className="px-3 py-1.5 border-b bg-slate-50 text-xs text-slate-600">
          Sugerencia: usa <kbd className="px-1 py-0.5 bg-white border rounded">Ctrl + F</kbd> del navegador para buscar en el documento.
          <button className="ml-3 underline" onClick={() => setShowSearch(false)}>cerrar</button>
        </div>
      )}
      {audiosError && (
        <div
          data-testid="audios-error-banner"
          className="px-3 py-1.5 border-b bg-amber-50 text-xs text-amber-900 flex items-center gap-2"
        >
          <Volume2 className="w-3 h-3" />
          <span className="flex-1">{audiosError}</span>
          <button
            className="underline"
            onClick={() => setAudiosError(null)}
            data-testid="audios-error-dismiss"
          >
            cerrar
          </button>
        </div>
      )}

      {/* Body: thumbnails + pages */}
      <div className="flex flex-1 min-h-0">
        {showThumbs && (
          <aside
            data-testid="pdf-thumbnails"
            className="w-32 shrink-0 border-r bg-slate-50 overflow-auto p-2 flex flex-col gap-2"
          >
            {Array.from({ length: numPages }).map((_, i) => (
              <button
                key={i}
                type="button"
                data-testid={`thumb-page-${i + 1}`}
                onClick={() => goToPage(i + 1)}
                className={`text-xs rounded border px-2 py-3 hover:bg-white ${currentPage === i + 1 ? 'bg-white border-sky-500 text-sky-700' : 'bg-slate-100 border-slate-200'}`}
              >
                Pág. {i + 1}
              </button>
            ))}
          </aside>
        )}
        <div
          ref={containerRef}
          className={containerClass}
          style={{ ...scrollContainerStyle, cursor: highlighterActive ? 'crosshair' : bodyCursor }}
        >
          {loading && <div className="p-6 text-sm text-slate-500">Cargando PDF…</div>}
          {loadError && <div className="p-6 text-sm text-red-600">Error: {loadError}</div>}
        </div>

        {/* Portal overlays: audio play buttons + highlights, per page */}
        {overlaysReady > 0 && Array.from({ length: numPages }).map((_, i) => {
          const pageNum = i + 1;
          const shell = pageRefs.current[pageNum];
          const overlay = shell?.querySelector('.pdf-page-overlays');
          if (!overlay) return null;
          const pageAudios = audios.filter(a => a.page === pageNum && a.kind !== 'EmbeddedFile');
          const pageHighlights = highlightsRef.current[pageNum] || [];
          const pageReviewFields = reviewFieldsByPage[pageNum] || [];
          const pageVisualHighlights = visualHighlightsByPage[pageNum] || [];
          // iter71.3 · Surgical hide of FH overlays that would land on
          // top of a `visual_highlight` (FIND / free annotation). We
          // compare **rect-by-rect** (per option / per FH student
          // highlight) instead of using the previous union-based
          // approach that was too coarse and hid neighbouring
          // exercises (e.g. 31.9 next to 31.8) whenever they shared a
          // graphic-question cluster.
          //
          // Rules (strict, only per-option):
          //   * `pageReviewHighlights`  — hide the individual FH
          //     `highlights.student[]` rect if it overlaps a visual
          //     highlight rect. Nothing else is affected.
          //   * `pageGraphicResults`    — never hide the whole question
          //     nor the Q1/Q2 pill. The per-option box render (below)
          //     skips only its OWN rect when it overlaps a visual
          //     highlight, so a FIND word inside an otherwise
          //     unrelated cluster no longer eats neighbouring
          //     questions.
          const _rectsOverlap = (a, b) => (
            Array.isArray(a) && Array.isArray(b) && a.length === 4 && b.length === 4 &&
            !(a[2] < b[0] || b[2] < a[0] || a[3] < b[1] || b[3] < a[1])
          );
          const _overlapsAnyVisual = (rect) => pageVisualHighlights.some(v => _rectsOverlap(rect, v?.rect));
          const pageReviewHighlights = (reviewHighlightsByPage[pageNum] || [])
            .filter(h => !_overlapsAnyVisual(h.rect));
          const pageGraphicResults = graphicResultsByPage[pageNum] || [];
          const pageAudioExercises = audioExercisesByPage[pageNum] || [];
          const statusColor = (s) => (
            s === 'correct' ? '#10b981' :        // emerald-500
            s === 'incorrect' ? '#ef4444' :      // red-500
            s === 'unanswered' ? '#94a3b8' :     // slate-400
            s === 'partial' ? '#f59e0b' :        // amber-500
            '#6b7280'
          );
          return createPortal(
            <React.Fragment key={pageNum}>
              {/* Highlighter capture layer (only active when tool is on) */}
              {highlighterActive && (
                <div
                  data-testid={`pdf-highlight-capture-${pageNum}`}
                  className="pdf-highlight-capture"
                  onMouseDown={(e) => onHighlightMouseDown(pageNum, e)}
                  style={{
                    position: 'absolute', inset: 0, cursor: 'crosshair',
                    zIndex: 4, background: 'transparent',
                  }}
                />
              )}
              {/* iter70 · Visual highlights (FIND / underlines / free
                  marks). Stored in PDF-points; render as a % of the
                  page shell using cached `pageDimsRef` — mirrors the
                  same conversion that student's session-only highlights
                  use, but the source of truth is the backend so these
                  marks survive across sessions and are visible to the
                  student, docente y admin en todas las vistas.
                  Interactividad: solo click-to-delete cuando el docente
                  está en review mode (`canManageVisualHighlights`). */}
              {(visualHighlightsByPage[pageNum] || []).map(h => {
                const dims = pageDimsRef.current[pageNum];
                const widthPt = dims?.widthPt || 0;
                const heightPt = dims?.heightPt || 0;
                if (!widthPt || !heightPt) return null;
                const clickable = canManageVisualHighlights;
                const onDelete = (e) => {
                  e.stopPropagation();
                  if (window.confirm('¿Eliminar este resaltado visual?')) {
                    postVisualHighlightAction('delete', { id: h.id, page: h.page, rect: h.rect, color: h.color });
                  }
                };
                // iter71.4 · A 'free' stroke with its original path (points,
                // PDF pt) renders as the exact line the alumno drew instead
                // of the rectangle covering its whole bounding box — that
                // rectangle used to swallow neighbouring, unmarked letters
                // in FIND / sopa de letras activities.
                if (h.kind === 'free' && Array.isArray(h.points) && h.points.length > 1) {
                  const shellNode = pageRefs.current[pageNum];
                  const pw = shellNode?.offsetWidth || 1;
                  const ph = shellNode?.offsetHeight || 1;
                  const dPath = h.points
                    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${(p[0] / widthPt) * pw} ${(p[1] / heightPt) * ph}`)
                    .join(' ');
                  const strokePx = ((h.thickness_pct || 3) / 100) * pw;
                  return (
                    <svg
                      key={h.id}
                      data-testid={`pdf-visual-highlight-${pageNum}-${h.id}`}
                      data-visual-highlight-id={h.id}
                      className="pdf-highlight-svg"
                      width="100%"
                      height="100%"
                      style={{
                        position: 'absolute', inset: 0, overflow: 'visible', zIndex: 5,
                        pointerEvents: 'none',
                      }}
                    >
                      <path
                        d={dPath}
                        fill="none"
                        stroke={h.color || '#22c55e'}
                        strokeOpacity={0.5}
                        strokeWidth={strokePx}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        onClick={clickable ? onDelete : undefined}
                        title={clickable ? 'Click para eliminar (docente)' : undefined}
                        style={{
                          pointerEvents: clickable ? 'stroke' : 'none',
                          cursor: clickable ? 'pointer' : 'default',
                        }}
                      />
                    </svg>
                  );
                }
                if (!Array.isArray(h.rect) || h.rect.length !== 4) return null;
                const [x0, y0, x1, y1] = h.rect;
                const xPct = (x0 / widthPt) * 100;
                const yPct = (y0 / heightPt) * 100;
                const wPct = ((x1 - x0) / widthPt) * 100;
                const hPct = ((y1 - y0) / heightPt) * 100;
                return (
                  <div
                    key={h.id}
                    data-testid={`pdf-visual-highlight-${pageNum}-${h.id}`}
                    data-visual-highlight-id={h.id}
                    onClick={clickable ? onDelete : undefined}
                    title={clickable ? 'Click para eliminar (docente)' : undefined}
                    style={{
                      position: 'absolute',
                      left: `${xPct}%`, top: `${yPct}%`,
                      width: `${wPct}%`, height: `${hPct}%`,
                      background: h.color || '#22c55e',
                      opacity: 0.45,
                      border: `1px solid ${h.color || '#22c55e'}`,
                      borderRadius: 2, zIndex: 5,
                      cursor: clickable ? 'pointer' : 'default',
                      pointerEvents: clickable ? 'auto' : 'none',
                    }}
                  />
                );
              })}
              {/* Persisted highlights */}
              {pageHighlights.map(h => {
                if (h.kind === 'free') {
                  // Convert stored % coords → CSS px using the shell's current size.
                  const shellNode = pageRefs.current[pageNum];
                  const pw = shellNode?.offsetWidth || 1;
                  const ppx = shellNode?.offsetHeight || 1;
                  const dPath = h.pointsPct
                    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${(p[0] * pw) / 100} ${(p[1] * ppx) / 100}`)
                    .join(' ');
                  const strokePx = (h.thicknessPct * pw) / 100;
                  return (
                    <svg
                      key={h.id}
                      data-testid={`pdf-highlight-${pageNum}-${h.id}`}
                      className="pdf-highlight-svg"
                      width="100%"
                      height="100%"
                      style={{
                        position: 'absolute', inset: 0, overflow: 'visible', zIndex: 5,
                        pointerEvents: 'none', // do not capture the whole page
                      }}
                    >
                      <path
                        className="pdf-highlight-path"
                        d={dPath}
                        fill="none"
                        stroke={h.color}
                        strokeOpacity={0.5}
                        strokeWidth={strokePx}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          pointerEvents: highlighterActive ? 'stroke' : 'none',
                          cursor: 'pointer',
                        }}
                        onClick={(e) => { e.stopPropagation(); removeHighlight(pageNum, h.id); }}
                      />
                    </svg>
                  );
                }
                return (
                  <div
                    key={h.id}
                    data-testid={`pdf-highlight-${pageNum}-${h.id}`}
                    onClick={(e) => { e.stopPropagation(); removeHighlight(pageNum, h.id); }}
                    title="Click para eliminar el resaltado"
                    className="pdf-highlight-chip"
                    style={{
                      position: 'absolute',
                      left: `${h.xPct}%`, top: `${h.yPct}%`,
                      width: `${h.wPct}%`, height: `${h.hPct}%`,
                      background: h.color,
                      opacity: 0.45,
                      border: `1px dashed ${h.color}`,
                      borderRadius: 2, zIndex: 5, cursor: 'pointer',
                      pointerEvents: highlighterActive ? 'auto' : 'none',
                    }}
                  />
                );
              })}
              {/* Live drawing preview */}
              {liveDraw && liveDraw.pageNum === pageNum && (
                liveDraw.kind === 'rect' ? (
                  liveDraw.width > 0 && (
                    <div style={{
                      position: 'absolute',
                      left: liveDraw.left, top: liveDraw.top,
                      width: liveDraw.width, height: liveDraw.height,
                      background: highlightColor, opacity: 0.35,
                      border: `1px solid ${highlightColor}`,
                      zIndex: 6, pointerEvents: 'none',
                    }} />
                  )
                ) : (
                  liveDraw.points.length > 1 && (
                    <svg
                      style={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%',
                        overflow: 'visible', zIndex: 6, pointerEvents: 'none',
                      }}
                    >
                      <path
                        d={liveDraw.points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ')}
                        fill="none"
                        stroke={highlightColor}
                        strokeOpacity={0.5}
                        strokeWidth={highlightThickness}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )
                )
              )}
              {/* ── Fase 2 · Correction overlays from Feedback Hub review ── */}
              {pageReviewFields.map(f => {
                const rect = f.rect || [f.x, f.y, (f.x || 0) + (f.w || f.width || 0), (f.y || 0) + (f.h || f.height || 0)];
                const [x0, y0, x1, y1] = rect.map(Number);
                const left = x0 * scale;
                const top = y0 * scale;
                const w = (x1 - x0) * scale;
                const h = (y1 - y0) * scale;
                // Iter68 · Decoupled colour/check for CheckBox and
                // RadioButton fields per Feedback Hub §9e contract:
                //   • COLOUR ← `is_answer` (teacher's expected answer)
                //     true  → 🟢 (this option is correct)
                //     false → 🔴 (distractor)
                //   • CHECK ← `selected` (student's mark)
                //     true  → ✓
                //     false → no mark
                // The global `status` is only used as the field label, NOT
                // as the colour source. Text / Dropdown / other fields
                // fall back to the legacy status-based colouring because
                // FH sends `is_answer: null` for them.
                const isBoolField = f.type === 'CheckBox' || f.type === 'RadioButton';
                const hasDecoupledContract = isBoolField
                  && (f.is_answer === true || f.is_answer === false);
                const color = hasDecoupledContract
                  ? (f.is_answer ? '#10b981' : '#ef4444')
                  : statusColor(f.status);
                const studentChecked = hasDecoupledContract
                  ? (Boolean(f.selected) || f.student_value === '☑')
                  : null; // ← null tells the pill to keep the legacy glyph
                const hasComment = Boolean(f.comment);
                const showComment = openCommentId === f._idx;
                // In correctionMode the box must NEVER swallow clicks that
                // belong to the AcroForm widget underneath — otherwise the
                // student cannot edit exactly the fields FH flagged. The
                // comment tooltip is still reachable via the small pill
                // (which keeps its own pointerEvents:'auto').
                // In teacher review the box is also clickable when
                // `allowOverride` is on, so the docente can open the
                // manual-status action panel.
                const boxClickable = (hasComment || allowOverride) && !correctionMode;
                // Fase 2 · Only the teacher (allowOverride=true) sees the
                // "modificado por docente" marker + FH original status.
                // Students receive the FINAL grade with no meta-info about
                // the teacher's editorial decision.
                const overridden = f.teacher_overridden === true;
                const showOverrideActions = allowOverride && !correctionMode;
                const showOverrideMarker = overridden && allowOverride;
                // Fase 2 iter48 · When the teacher has overridden the field
                // to «Correcto», any FH message that contradicts the new
                // verdict (comment «Incorrecta. Respuesta correcta: X»,
                // or the «Esperado: X» line) must be HIDDEN from the
                // student. The teacher still sees them as reference.
                const hideFhForStudent = overridden && !allowOverride && f.status === 'correct';
                return (
                  <div
                    key={`rev-${f._idx}`}
                    data-testid={`pdf-review-field-${f._idx}`}
                    data-review-status={f.status}
                    data-review-overridden={overridden ? '1' : '0'}
                    style={{
                      position: 'absolute',
                      left, top, width: Math.max(w, 8), height: Math.max(h, 8),
                      border: `2px ${showOverrideMarker ? 'dashed' : 'solid'} ${color}`,
                      background: `${color}22`,
                      borderRadius: 2,
                      // Fase 2 iter47 · Lift the active field above its
                      // siblings while its tooltip is open, so the tooltip
                      // action buttons («Marcar Correcto» / «Revertir a
                      // Feedback Hub») are never covered by later overlay
                      // divs painted after this one in the DOM.
                      zIndex: showComment ? 30 : 7,
                      pointerEvents: boxClickable ? 'auto' : 'none',
                      cursor: boxClickable ? 'pointer' : 'default',
                    }}
                    onClick={boxClickable ? (e) => { e.stopPropagation(); setOpenCommentId(showComment ? null : f._idx); } : undefined}
                    title={hideFhForStudent
                      ? 'Correcto'
                      : (f.comment || `${f.status}${f.correct_value ? ` · Esperado: ${f.correct_value}` : ''}${showOverrideMarker ? ' · Modificado por docente' : ''}`)}
                  >
                    {/* Small status pill top-right — clickable even in
                        correctionMode so the student can still peek at
                        the FH comment without blocking widget edits, and
                        also serves as the entry point for the teacher's
                        override action panel. */}
                    <span
                      data-testid={`pdf-review-pill-${f._idx}`}
                      style={{
                        position: 'absolute', right: -1, top: -12,
                        background: color, color: '#fff',
                        fontSize: 9, lineHeight: 1, padding: '2px 4px',
                        borderRadius: 3, whiteSpace: 'nowrap',
                        fontWeight: 600,
                        pointerEvents: (hasComment || showOverrideActions) ? 'auto' : 'none',
                        cursor: (hasComment || showOverrideActions) ? 'pointer' : 'default',
                      }}
                      onClick={(hasComment || showOverrideActions) ? (e) => { e.stopPropagation(); setOpenCommentId(showComment ? null : f._idx); } : undefined}
                    >
                      {studentChecked !== null
                        ? (studentChecked ? (f.is_answer ? '✓' : '✗') : '·')
                        : (f.status === 'correct' ? '✓' : f.status === 'incorrect' ? '✗' : f.status === 'partial' ? '~' : '·')
                      }{showOverrideMarker ? 'M' : ''}
                    </span>
                    {showComment && (hasComment || showOverrideActions) && (
                      <div
                        data-testid={`pdf-review-comment-${f._idx}`}
                        style={{
                          position: 'absolute', top: '100%', left: 0, marginTop: 4,
                          background: '#111827', color: '#fff', fontSize: 11,
                          padding: '6px 8px', borderRadius: 4, minWidth: 160, maxWidth: 280,
                          zIndex: 15, boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div style={{ fontWeight: 600, marginBottom: 2 }}>
                          {f.status === 'correct' ? 'Correcto' : f.status === 'incorrect' ? 'Incorrecto' : f.status === 'partial' ? 'Parcial' : 'Sin responder'}
                          {showOverrideMarker && (
                            <span style={{ marginLeft: 6, fontWeight: 500, opacity: 0.85 }}>· Modificado por docente</span>
                          )}
                        </div>
                        {showOverrideMarker && f.original_status && f.original_status !== f.status && (
                          <div style={{ opacity: 0.75, fontStyle: 'italic', marginBottom: 4 }}>
                            Feedback Hub: {f.original_status === 'correct' ? 'Correcto' : f.original_status === 'incorrect' ? 'Incorrecto' : f.original_status === 'partial' ? 'Parcial' : 'Sin responder'}
                          </div>
                        )}
                        {(f.correct_value || (f.status === 'unanswered' && (f.comment || '').match(/Respuesta correcta:\s*(.+)$/))) && !hideFhForStudent && (
                          <div style={{ opacity: 0.85 }}>
                            Esperado: <b>{f.correct_value || ((f.comment || '').match(/Respuesta correcta:\s*(.+)$/) || [])[1]}</b>
                          </div>
                        )}
                        {f.comment && !hideFhForStudent && f.status !== 'incorrect' && f.status !== 'unanswered' && (
                          <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{f.comment}</div>
                        )}
                        {showOverrideActions && (
                          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                            {f.status !== 'correct' && (
                              <button
                                type="button"
                                data-testid={`override-correct-${f._idx}`}
                                onClick={() => applyOverride(f, 'correct')}
                                style={{
                                  background: '#059669', color: '#fff', border: 'none',
                                  padding: '4px 8px', borderRadius: 4, fontSize: 11,
                                  cursor: 'pointer', fontWeight: 600,
                                }}
                              >
                                Marcar Correcto
                              </button>
                            )}
                            {overridden && (
                              <button
                                type="button"
                                data-testid={`override-revert-${f._idx}`}
                                onClick={() => applyOverride(f, null)}
                                style={{
                                  background: 'transparent', color: '#e5e7eb',
                                  border: '1px solid #4b5563',
                                  padding: '4px 8px', borderRadius: 4, fontSize: 11,
                                  cursor: 'pointer', fontWeight: 500,
                                }}
                              >
                                Revertir a Feedback Hub
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* ── Fase 2 · Persisted student highlighter marks from Feedback Hub
                  review (review.highlights.student[]). Rendered directly from the
                  review payload — NOT from the session-only highlightsRef, which is
                  never populated in readOnly/teacher review mode. Same PDF-point,
                  top-left-origin rect + `scale` multiplication as the review.fields
                  overlay above. ── */}
              {pageReviewHighlights.map(h => {
                const rect = h.rect || [];
                const [x0, y0, x1, y1] = rect.map(Number);
                const left = x0 * scale;
                const top = y0 * scale;
                const w = (x1 - x0) * scale;
                const hgt = (y1 - y0) * scale;
                return (
                  <div
                    key={`revhl-${h._idx}`}
                    data-testid={`pdf-review-highlight-${h._idx}`}
                    style={{
                      position: 'absolute',
                      left, top, width: Math.max(w, 2), height: Math.max(hgt, 2),
                      background: '#facc15', opacity: 0.35,
                      border: '1px solid #facc15',
                      borderRadius: 2,
                      zIndex: 6,
                      pointerEvents: 'none',
                    }}
                  />
                );
              })}
              {/* ── Fase 2 · Feedback Hub graphic answers (Tipo B / CROSS OUT).
                  Renders one status overlay per question, positioned over the
                  CORRECT option's rect (or the union of option rects as
                  fallback). The teacher (`allowOverride=true`) can flip the
                  status to Correcto or revert to FH via the same tooltip
                  affordance used for AcroForms. Never calls Feedback Hub.
                  ── */}
              {pageGraphicResults.map(g => {
                const rect = g._overlayRect;
                if (!rect) return null;
                const [x0, y0, x1, y1] = rect.map(Number);
                const left = x0 * scale;
                const top = y0 * scale;
                const w = (x1 - x0) * scale;
                const h = (y1 - y0) * scale;
                const opts = Array.isArray(g.options) ? g.options : [];
                // Iter68 · Detect Feedback Hub §9e decoupled contract:
                // options[] carrying `is_answer` and `selected` per option
                // (arrived after re-ingesting the master PDF into FH). Old
                // reviews without this field fall back to the single
                // per-question overlay so they keep working.
                const hasDecoupledContract = opts.some(o => o && 'is_answer' in o);
                const selectedIds = Array.isArray(g.selected_option_ids)
                  ? g.selected_option_ids
                  : (g.selected_option_id ? [g.selected_option_id] : []);
                const correctIds = Array.isArray(g.correct_option_ids)
                  ? g.correct_option_ids
                  : (g.correct_option_id ? [g.correct_option_id] : []);
                const overridden = g.teacher_overridden === true;
                const showOverrideActions = allowOverride && !correctionMode;
                const showOverrideMarker = overridden && allowOverride;
                const gTestKey = `g-${g._idx}`;
                const showComment = openCommentId === gTestKey;
                const statusColorHex = statusColor(g.status);
                const statusLabel = (
                  g.status === 'correct' ? 'Correcto'
                  : g.status === 'incorrect' ? 'Incorrecto'
                  : g.status === 'partial' ? 'Parcial'
                  : 'Sin responder'
                );
                const overallClickable = (
                  (showOverrideActions || overridden || selectedIds.length > 0)
                  && !correctionMode
                );
                const statusGlyph = (
                  g.status === 'correct' ? '✓'
                  : g.status === 'incorrect' ? '✗'
                  : g.status === 'partial' ? '~'
                  : '·'
                );
                // Iter72.5c · CROSS OUT badge anchoring:
                //  1. If the student marked an option (either `selected` on
                //     the decoupled contract or `selected_option_id(s)`), the
                //     badge anchors to THAT option's rect.
                //  2. If the student left it unanswered, it anchors to the
                //     option that SHOULD have been crossed out (`is_answer` /
                //     `correct_option_id(s)`), which is what already worked on
                //     Q6/Q10/Q11 by luck of the union rect.
                //  3. No usable rect → original per-question corner.
                // The badge sits ON the TOP-RIGHT CORNER of that rect (not
                // centred) so the student's own mark stays readable.
                const hasRect = (o) => o && Array.isArray(o.rect) && o.rect.length === 4;
                const anchorRect = (() => {
                  const selOpt = opts.find(o => hasRect(o) && (o.selected === true || selectedIds.includes(o.option_id)));
                  if (selOpt) return selOpt.rect;
                  if (Array.isArray(g._selectedRect) && g._selectedRect.length === 4) return g._selectedRect;
                  const ansOpt = opts.find(o => hasRect(o) && (o.is_answer === true || correctIds.includes(o.option_id)));
                  if (ansOpt) return ansOpt.rect;
                  return null;
                })();
                const pillStyle = anchorRect
                  ? (() => {
                    const [ax0, ay0] = anchorRect.map(Number);
                    const ax1 = Number(anchorRect[2]);
                    return {
                      left: ax1 * scale - left,
                      top: ay0 * scale - top,
                      transform: 'translate(-100%, -100%)',
                    };
                  })()
                  : { right: -1, top: -12 };
                return (
                  <React.Fragment key={`rev-graphic-${g._idx}`}>
                    {/* Iter68 · Per-option colour boxes (only when the
                        decoupled contract is present). Each rectangle is
                        painted with the colour dictated by the option's
                        own `is_answer`; the student's action (`selected`)
                        is shown as a subtle diagonal strike-through so
                        the teacher immediately sees BOTH sources of truth
                        (expected vs. actual) without confusing them. */}
                    {hasDecoupledContract && opts.map((opt, oi) => {
                      if (!opt || !Array.isArray(opt.rect) || opt.rect.length !== 4) return null;
                      // iter71.3 · Surgical per-option skip: if THIS
                      // specific option rect overlaps a visual
                      // highlight (FIND word), we don't paint its
                      // coloured box — but sibling options in the
                      // same graphic question keep rendering
                      // untouched (fixes the 31.8 ⇄ 31.9 bleed).
                      if (_overlapsAnyVisual(opt.rect)) return null;
                      const [ox0, oy0, ox1, oy1] = opt.rect.map(Number);
                      const oLeft = ox0 * scale;
                      const oTop = oy0 * scale;
                      const oW = Math.max((ox1 - ox0) * scale, 8);
                      const oH = Math.max((oy1 - oy0) * scale, 8);
                      const optCorrect = Boolean(opt.is_answer);
                      const optSelected = Boolean(opt.selected);
                      const optColor = optCorrect ? '#10b981' : '#ef4444';
                      return (
                        <div
                          key={`rev-graphic-opt-${g._idx}-${oi}`}
                          data-testid={`pdf-review-graphic-option-${g._idx}-${oi}`}
                          data-graphic-option-id={opt.option_id || ''}
                          data-graphic-option-is-answer={optCorrect ? '1' : '0'}
                          data-graphic-option-selected={optSelected ? '1' : '0'}
                          style={{
                            position: 'absolute',
                            left: oLeft, top: oTop, width: oW, height: oH,
                            border: `2px solid ${optColor}`,
                            background: `${optColor}22`,
                            borderRadius: 3,
                            zIndex: 6,
                            pointerEvents: 'none',
                          }}
                        >
                          {optSelected && (
                            <svg
                              width={oW} height={oH}
                              style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
                              viewBox={`0 0 ${oW} ${oH}`}
                              preserveAspectRatio="none"
                              aria-hidden="true"
                            >
                              <line x1={2} y1={oH - 2} x2={oW - 2} y2={2} stroke={optColor} strokeWidth={2.5} />
                            </svg>
                          )}
                        </div>
                      );
                    })}
                    {/* Per-question anchor for status pill, tooltip and
                        override actions. Transparent when the decoupled
                        contract paints per-option boxes (otherwise it
                        would recolour the whole cluster with the global
                        status = the "todo verde" bug the user reported). */}
                    <div
                      data-testid={`pdf-review-graphic-${g._idx}`}
                      data-graphic-status={g.status}
                      data-graphic-overridden={overridden ? '1' : '0'}
                      data-graphic-page={g.page}
                      data-graphic-question={g.question}
                      style={{
                        position: 'absolute',
                        left, top,
                        width: Math.max(w, 8), height: Math.max(h, 8),
                        border: hasDecoupledContract ? 'none' : `2px ${showOverrideMarker ? 'dashed' : 'solid'} ${statusColorHex}`,
                        background: hasDecoupledContract ? 'transparent' : `${statusColorHex}22`,
                        borderRadius: 3,
                        zIndex: showComment ? 30 : 7,
                        pointerEvents: overallClickable ? 'auto' : 'none',
                        cursor: overallClickable ? 'pointer' : 'default',
                      }}
                      onClick={overallClickable ? (e) => {
                        e.stopPropagation();
                        setOpenCommentId(showComment ? null : gTestKey);
                      } : undefined}
                      title={`Q${g.question} · ${statusLabel}${showOverrideMarker ? ' · Modificado por docente' : ''}`}
                    >
                      <span
                        data-testid={`pdf-review-graphic-pill-${g._idx}`}
                        style={{
                          position: 'absolute', ...pillStyle,
                          background: statusColorHex, color: '#fff',
                          fontSize: 9, lineHeight: 1, padding: '2px 4px',
                          borderRadius: 3, whiteSpace: 'nowrap',
                          fontWeight: 600,
                          pointerEvents: overallClickable ? 'auto' : 'none',
                          cursor: overallClickable ? 'pointer' : 'default',
                        }}
                        onClick={overallClickable ? (e) => {
                          e.stopPropagation();
                          setOpenCommentId(showComment ? null : gTestKey);
                        } : undefined}
                      >
                        Q{g.question} {statusGlyph}{showOverrideMarker ? 'M' : ''}
                      </span>
                      {showComment && (
                        <div
                          data-testid={`pdf-review-graphic-comment-${g._idx}`}
                          style={{
                            position: 'absolute', top: '100%', left: 0, marginTop: 4,
                            background: '#111827', color: '#fff', fontSize: 11,
                            padding: '6px 8px', borderRadius: 4, minWidth: 180, maxWidth: 300,
                            zIndex: 15, boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div style={{ fontWeight: 600, marginBottom: 2 }}>
                            {`Pregunta ${g.question} · ${statusLabel}`}
                            {typeof g.expected_count === 'number' && g.expected_count > 1 && (
                              <span style={{ marginLeft: 6, fontWeight: 500, opacity: 0.85 }}>
                                · {selectedIds.length}/{g.expected_count} tachadas
                              </span>
                            )}
                            {showOverrideMarker && (
                              <span style={{ marginLeft: 6, fontWeight: 500, opacity: 0.85 }}>· Modificado por docente</span>
                            )}
                          </div>
                          {showOverrideMarker && g.original_status && g.original_status !== g.status && (
                            <div style={{ opacity: 0.75, fontStyle: 'italic', marginBottom: 4 }}>
                              Feedback Hub: {g.original_status === 'correct' ? 'Correcto' : g.original_status === 'incorrect' ? 'Incorrecto' : g.original_status === 'partial' ? 'Parcial' : 'Sin responder'}
                            </div>
                          )}
                          {allowOverride && selectedIds.length > 0 && (
                            <div style={{ opacity: 0.85 }}>
                              Alumno seleccionó: <b>{selectedIds.join(', ')}</b>
                            </div>
                          )}
                          {allowOverride && correctIds.length > 0 && (
                            <div style={{ opacity: 0.85 }}>
                              {correctIds.length > 1 ? 'Correctas' : 'Correcta'}: <b>{correctIds.join(', ')}</b>
                            </div>
                          )}
                          {showOverrideActions && (
                            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                              {g.status !== 'correct' && (
                                <button
                                  type="button"
                                  data-testid={`graphic-override-correct-${g._idx}`}
                                  onClick={() => applyGraphicOverride(g, 'correct')}
                                  style={{
                                    background: '#059669', color: '#fff', border: 'none',
                                    padding: '4px 8px', borderRadius: 4, fontSize: 11,
                                    cursor: 'pointer', fontWeight: 600,
                                  }}
                                >
                                  Marcar Correcto
                                </button>
                              )}
                              {overridden && (
                                <button
                                  type="button"
                                  data-testid={`graphic-override-revert-${g._idx}`}
                                  onClick={() => applyGraphicOverride(g, null)}
                                  style={{
                                    background: 'transparent', color: '#e5e7eb',
                                    border: '1px solid #4b5563',
                                    padding: '4px 8px', borderRadius: 4, fontSize: 11,
                                    cursor: 'pointer', fontWeight: 500,
                                  }}
                                >
                                  Revertir a Feedback Hub
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
              {/* Fase 3 · Recorder overlays over each Feedback Hub audio
                  exercise. Only rendered when we have an `audioMode` (i.e.
                  student post-submit, teacher reviewing, student viewing
                  a published correction, OR student pre-submit with
                  cached bank-level audio_exercises). Coordinates come
                  exclusively from FH — AE never detects mics. */}
              {audioMode && pageAudioExercises.map(exercise => (
                <AudioExerciseOverlay
                  key={`audio-exercise-${exercise.exercise_id}`}
                  exercise={exercise}
                  scale={scale}
                  mode={audioMode}
                  activityId={audioActivityId}
                  API={API}
                  token={token}
                  onUploaded={handleAudioUploaded}
                  onLocalBlobChange={handlePreSubmitBlobChange}
                  isOpen={openRecorderId === exercise.exercise_id}
                  onOpenChange={(next) => setOpenRecorderId(next ? exercise.exercise_id : null)}
                  sidebarHost={recorderSidebarHost}
                />
              ))}
              {/* Audio play-buttons */}
              {pageAudios.map(a => {
                const size = Math.max(28, Math.min(a.w * scale, 44));
                return (
                  <button
                    key={a.index}
                    type="button"
                    data-testid={`pdf-audio-btn-${a.index}`}
                    title={a.has_bytes === false ? 'Audio no extraíble' : `Reproducir ${a.label || `audio ${a.index + 1}`}`}
                    onClick={() => openAudio(a)}
                    className="pdf-audio-btn"
                    style={{
                      position: 'absolute',
                      left: a.x * scale,
                      top: a.y * scale,
                      width: a.w * scale || size,
                      height: a.h * scale || size,
                      minWidth: 28, minHeight: 28,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: a.has_bytes === false ? 'rgba(148,163,184,0.85)' : 'rgba(16,185,129,0.9)',
                      color: '#fff',
                      border: '2px solid #fff',
                      borderRadius: '50%',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                      cursor: a.has_bytes === false ? 'not-allowed' : 'pointer',
                      zIndex: 6,
                    }}
                  >
                    <Play className="w-4 h-4" />
                  </button>
                );
              })}
            </React.Fragment>,
            overlay
          );
        })}
      </div>

      {/* iter69 · Sidebar host for the AudioExerciseOverlay recorder
          panel. Positioned outside the scrolling pages container so the
          panel stays anchored to the top-right of the viewer while the
          student scrolls the PDF, exactly like the audio-listening
          player above. The div is always mounted (React needs a stable
          host for `createPortal`); it's just empty when no recorder is
          open. Located below the top audio player to avoid collision. */}
      <div
        ref={setRecorderSidebarHost}
        data-testid="pdf-recorder-sidebar-host"
        className="absolute right-3 top-40 z-40"
        style={{ pointerEvents: openRecorderId ? 'auto' : 'none' }}
      />

      {/* Floating audio player (top-right, over the toolbar area to avoid covering PDF text) */}
      {activeAudio && (
        <div
          data-testid="pdf-audio-player"
          className="absolute right-3 top-24 z-40 flex items-center gap-2 bg-slate-900 text-white rounded-full shadow-lg pl-2 pr-1 py-1"
          style={{ maxWidth: '92%' }}
        >
          <Volume2 className="w-4 h-4 text-emerald-400 shrink-0" />
          <button
            type="button"
            onClick={toggleAudio}
            data-testid="pdf-audio-toggle"
            className="w-8 h-8 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center"
          >
            {audioPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <div className="text-xs truncate max-w-[160px]">{activeAudio.label}</div>
          <audio
            ref={audioElRef}
            onPlay={() => setAudioPlaying(true)}
            onPause={() => setAudioPlaying(false)}
            onEnded={() => setAudioPlaying(false)}
            hidden
          />
          <button
            type="button"
            onClick={closeAudio}
            data-testid="pdf-audio-close"
            className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export default PdfActivityViewer;
